/* ══════════════════════════════════════════════════════════════════════
   Trasse Console — pixel-faithful reproduction of the approved design.
   Renders entirely from a ConsoleData object (demo or live). All visual
   styling is ported 1:1 from the design (inline styles + CSS-var theme).
   Live data & real actions are injected via props (see useConsoleData).
   ══════════════════════════════════════════════════════════════════════ */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import './consoleTheme.css';
import {
  ConsoleData, DEMO_DATA, ICON, Org, OrgStatus, Admin, AdminStatus, Bus,
  AddonKey, AddonConn, ADDON_CATALOG, addonDef, EstimatedPriceOrg, PlatformAlert,
} from './consoleModel';
import LocationPicker, { LatLng } from '../components/LocationPicker';
import type { RouteLearningStatus, RouteLearningStatusTrip } from '../services/consoleApi';
import { fetchWorkingHours, setWorkingHours } from '../services/consoleApi';
import { guardRemoval } from '../services/orgLifecycle';
import ZanaCapPanel from './ZanaCapPanel';
import ZanaPlatformPanel from './ZanaPlatformPanel';
import { isFamilyOrg, isLorryOrg } from '../fleet/fleetConstants';
import '../site-v3/legacy-v3.css'; // Barlow, the trasseapp.com face (2026-10-28)

type CSS = React.CSSProperties;
type Vars = Record<string, string>;

/* ── accents ── Teal first and the default: the trasseapp.com design
   (Gabriel, 2026-10-28). The others stay as choices in Settings. */
const ACCENTS: Record<string, { name: string; accent: string; strong: string; soft: string; tint: string; swatch: string }> = {
  teal:    { name: 'Teal',    accent: '#00707F', strong: '#005A66', soft: '#E2F5F8', tint: '#CDEFF4', swatch: '#0FB8CE' },
  indigo:  { name: 'Indigo',  accent: '#5b52e0', strong: '#4840c4', soft: '#eeecfb', tint: '#ded9f8', swatch: '#5b52e0' },
  emerald: { name: 'Emerald', accent: '#0f9d58', strong: '#0b7d45', soft: '#e6f4ec', tint: '#c9e8d5', swatch: '#0f9d58' },
  blue:    { name: 'Blue',    accent: '#2f7ae5', strong: '#245fc0', soft: '#e9f1fd', tint: '#cfe0fa', swatch: '#2f7ae5' },
  orange:  { name: 'Orange',  accent: '#e5642a', strong: '#c44f1c', soft: '#fdeee4', tint: '#f8d6c3', swatch: '#e5642a' },
};

function buildTheme(dark: boolean, accentKey: string): Vars {
  const a = ACCENTS[accentKey] || ACCENTS.teal;
  const dyn: Vars = dark ? {
    '--bg': '#0B1116', '--surface': '#121A21', '--sunken': '#0E151B', '--soft': '#161F27', '--border': '#24303A', '--hairline': '#1C262F',
    '--text': '#EEF3F5', '--t2': '#C3CDD3', '--t3': '#9AA9B4', '--t4': '#6C7A85',
    '--green-soft': 'color-mix(in oklab,#1E7A4C 26%,#0B1116)', '--amber-soft': 'color-mix(in oklab,#9A5B00 26%,#0B1116)', '--red-soft': 'color-mix(in oklab,#B42318 26%,#0B1116)', '--info-soft': 'color-mix(in oklab,#00707F 26%,#0B1116)',
  } : {
    '--bg': '#F6F5F1', '--surface': '#ffffff', '--sunken': '#EFEDE7', '--soft': '#FAF9F6', '--border': '#E4E3DE', '--hairline': '#ECEAE5',
    '--text': '#0F1419', '--t2': '#2A3138', '--t3': '#5D646B', '--t4': '#8C9196',
    '--green-soft': '#E4F1E9', '--amber-soft': '#FBEFD9', '--red-soft': '#FDECEA', '--info-soft': '#E2F5F8',
  };
  return {
    ...dyn,
    '--accent': a.accent, '--accent-strong': a.strong,
    '--accent-soft': dark ? `color-mix(in oklab, ${a.accent} 22%, #121A21)` : a.soft,
    '--accent-tint': a.tint, '--accent-fg': '#ffffff',
    '--nav-bg': '#0F1419', '--nav-bg2': '#1A2128', '--nav-line': '#252D35', '--nav-text': '#C9D1D6', '--nav-muted': '#7F8A92',
    '--green': '#1E7A4C', '--amber': '#9A5B00', '--red': '#B42318', '--info': '#00707F',
    '--r-sm': '7px', '--r': '10px', '--r-lg': '14px', '--r-xl': '20px',
    '--sh-xs': '0 1px 2px rgba(20,20,35,0.05),0 0 0 1px rgba(20,20,35,0.04)',
    '--sh-sm': '0 2px 8px rgba(20,20,35,0.06),0 0 0 1px rgba(20,20,35,0.04)',
    '--sh-md': '0 12px 30px -12px rgba(20,20,35,0.16),0 0 0 1px rgba(20,20,35,0.05)',
    '--sh-lg': '0 30px 60px -18px rgba(20,20,35,0.34),0 0 0 1px rgba(20,20,35,0.06)',
  };
}

/* ── tiny icon helper ── */
const Ico: React.FC<{ d: string; size?: number; sw?: number; fill?: string; stroke?: string; style?: CSS }> = ({ d, size = 18, sw = 1.9, fill = 'none', stroke = 'currentColor', style }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill={fill} stroke={stroke} strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" style={style}>
    <path d={d} />
  </svg>
);
const Star: React.FC<{ fill: string; size?: number }> = ({ fill, size = 12 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill={fill} stroke="none"><path d={ICON.star} /></svg>
);

/* ── chip / color helpers (ported from design) ── */
const AV_COLORS = ['var(--accent)', 'var(--info)', 'var(--green)', 'var(--amber)', 'var(--red)'];
const initialsOf = (name: string) => name.split(' ').slice(0, 2).map(w => w[0] || '').join('');

function orgStatusChip(st: OrgStatus): CSS {
  const m: Record<string, string> = { Active: 'var(--green)', Trial: 'var(--info)', Pending: 'var(--amber)', Expired: 'var(--red)', Suspended: 'var(--amber)', Canceling: 'var(--red)' };
  const c = m[st] || 'var(--t3)';
  return { display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '11.5px', fontWeight: 700, padding: '3px 10px', borderRadius: '999px', color: c, background: `color-mix(in oklab,${c} 14%, transparent)` };
}
function adminStatusChip(st: AdminStatus): CSS {
  const m: Record<string, string> = { Active: 'var(--green)', Pending: 'var(--amber)', Inactive: 'var(--t3)' };
  const c = m[st] || 'var(--t3)';
  return { display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '11.5px', fontWeight: 700, padding: '3px 10px', borderRadius: '999px', color: c, background: `color-mix(in oklab,${c} 14%, transparent)` };
}
function roleChip(role: string): CSS {
  const kind = role.includes('Owner') || role.includes('Console') ? 'Owner' : 'Admin';
  const m: Record<string, string> = { Owner: 'var(--accent)', Admin: 'var(--info)' };
  const c = m[kind];
  return { fontSize: '11.5px', fontWeight: 700, padding: '3px 10px', borderRadius: '999px', color: c, background: `color-mix(in oklab,${c} 14%, transparent)` };
}
function busStatusChip(c: string): CSS {
  return { display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '11px', fontWeight: 700, padding: '3px 9px', borderRadius: '999px', color: c, background: `color-mix(in oklab,${c} 14%, transparent)` };
}
const starsFor = (rating: number) => [0, 1, 2, 3, 4].map(i => (i < Math.round(rating) ? 'var(--amber)' : 'var(--border)'));

/* ── revenue area chart path from points ── */
function revenuePaths(points: number[]) {
  const w = 720, top = 30, bottom = 185, base = 200;
  const n = points.length || 1;
  const max = Math.max(...points, 1), min = Math.min(...points, 0);
  const span = max - min || 1;
  const xs = (i: number) => (n === 1 ? w : (i * (w / (n - 1))));
  const ys = (v: number) => bottom - ((v - min) / span) * (bottom - top);
  const line = points.map((v, i) => `${i ? 'L' : 'M'}${xs(i).toFixed(0)},${ys(v).toFixed(0)}`).join(' ');
  const area = `${line} L${w},${base} L0,${base} Z`;
  return { line, area, lastX: xs(n - 1), lastY: ys(points[n - 1] ?? 0) };
}

/* ── plan donut arcs ── */
function donutArcs(premium: number, standard: number, trial: number) {
  const R = 48, C = 2 * Math.PI * R, gap = 6;
  const total = premium + standard + trial || 1;
  const seg = (v: number) => (v / total) * C;
  const p = seg(premium), s = seg(standard), t = seg(trial);
  const arc = (len: number, offsetAt: number, color: string) => ({
    stroke: color,
    strokeDasharray: `${Math.max(0, len - gap)} ${C}`,
    strokeDashoffset: `${-offsetAt}`,
  });
  return [
    arc(p, 0, 'var(--accent)'),
    arc(s, p, 'var(--info)'),
    arc(t, p + s, 'var(--amber)'),
  ];
}

/* ── nav definition ── */
type Tab = 'dashboard' | 'organizations' | 'users' | 'content' | 'calendar' | 'reports' | 'bus' | 'lorry' | 'analytics';
const NAV: { label: string; items: { id: Tab; label: string; d: string; count?: string }[] }[] = [
  { label: 'Main', items: [
    { id: 'dashboard', label: 'Dashboard', d: 'M3 13h4v7H3zM10 4h4v16h-4zM17 9h4v11h-4z' },
    { id: 'organizations', label: 'Organizations', count: '', d: 'M12 3 2 8l10 5 10-5zM2 16l10 5 10-5M2 12l10 5 10-5' },
    { id: 'users', label: 'Users', count: '', d: 'M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8M23 21v-2a4 4 0 0 0-3-3.9M16 3.1a4 4 0 0 1 0 7.8' },
  ]},
  { label: 'Content', items: [
    { id: 'content', label: 'Content', d: 'M4 4h16v14H4zM4 9h16M9 18v3M15 18v3' },
    { id: 'calendar', label: 'Calendar', d: 'M4 5h16v16H4zM4 9h16M8 3v4M16 3v4' },
    { id: 'reports', label: 'Reports', d: 'M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9zM14 3v6h6M8 13h8M8 17h5' },
  ]},
  { label: 'Fleet', items: [
    { id: 'bus', label: 'Bus Tracking', count: '', d: 'M4 16V6a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v10M4 16h16M4 16v2M20 16v2M4 8h16M8 20h.01M16 20h.01' },
    { id: 'lorry', label: 'Lorry Fleets', d: 'M3 6h11v9H3zM14 9h4l3 3v3h-7zM7 18a2 2 0 1 0 0-.01M18 18a2 2 0 1 0 0-.01' },
  ]},
  { label: 'Insights', items: [
    { id: 'analytics', label: 'Analytics', d: 'M3 3v18h18M7 15l3-4 3 2 5-7' },
  ]},
];
const TITLES: Record<Tab, [string, string]> = {
  dashboard: ['Dashboard', 'Real-time overview across every school'],
  organizations: ['Organizations', 'Every school on the Trasse platform'],
  users: ['Users', 'Console & school admins, grouped by school'],
  content: ['Content', 'Menus, announcements and media'],
  reports: ['Reports', 'Export and schedule platform reports'],
  bus: ['Bus Tracking', 'All buses, grouped by school'],
  lorry: ['Lorry Fleets', 'Cargo vehicles and logistics'],
  calendar: ['Calendar', 'Events, terms and holidays'],
  analytics: ['Analytics', 'Fuel, distance, ratings & revenue by school'],
};

/* ── toast type ── */
type ToastKind = 'success' | 'error' | 'info';
interface Toast { id: number; kind: ToastKind; title: string; msg: string; }
const TOAST_CFG: Record<ToastKind, { c: string; d: string }> = {
  success: { c: 'var(--green)', d: 'M20 6L9 17l-5-5' },
  error: { c: 'var(--red)', d: 'M12 9v4M12 17h.01M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z' },
  info: { c: 'var(--info)', d: 'M12 16v-4M12 8h.01' },
};

/* ── wizard plan/kind config (mirrors client signup) ── */
const WKINDS = [
  { id: 'FULL_FEATURE', title: 'Full feature', desc: 'Schools — students, teachers, parents, diary, menus and more.', roles: 'Admin · Teacher · Parent · Student', suffix: '—', grad: 'linear-gradient(135deg,#9b7cff,#6f4ff0)', d: 'M22 10 12 5 2 10l10 5 10-5zM6 12v5c3 2 9 2 12 0v-5' },
  { id: 'BUS_TRACKING', title: 'Bus tracking only', desc: 'School bus routes — fleet admin, bus driver, parent.', roles: 'Fleet Admin · Bus Driver · Parent', suffix: '.bus', grad: 'linear-gradient(135deg,#22c3ee,#2f7cf0)', d: 'M4 16V6a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v10M4 16h16M4 8h16M8 20h.01M16 20h.01' },
  { id: 'LORRY', title: 'Lorry', desc: 'Commercial fleet — personal tracking or cargo with clients.', roles: 'Owner · Driver · Client', suffix: '.fleet / .cargo', grad: 'linear-gradient(135deg,#ffb27a,#f5872f)', d: 'M3 6h11v9H3zM14 9h4l3 3v3h-7zM7 18a2 2 0 1 0 0 .01M18 18a2 2 0 1 0 0 .01' },
] as const;
/* Plans mirror the real subscriptionModel: schools bill per child/term
   (350/550/750), lorry per vehicle/mo (250/450/650). maxStudents 0 = unlimited.
   These defaults prefill the limit caps; the admin can tighten them. */
const WPLANS = [
  { id: 'premium', name: 'Premium', schoolPrice: '650', lorryPrice: '450', maxStudents: 500, maxTeachers: 25, maxParents: 1000, limits: '500 students · 25 teachers · 1,000 parents' },
  { id: 'enterprise', name: 'Enterprise', schoolPrice: '850', lorryPrice: '650', maxStudents: 0, maxTeachers: 0, maxParents: 0, limits: 'Unlimited students, teachers & parents' },
] as const;
const planPrice = (pl: typeof WPLANS[number], isLorry: boolean) => isLorry ? pl.lorryPrice : pl.schoolPrice;
const planUnitSuffix = (isLorry: boolean) => isLorry ? '/vehicle/mo' : '/child/term';

/* ══════════════════════════════════════════════════════════════════════ */

export interface ConsoleActions {
  onLogout?: () => void;
  onGenerateReport?: (title: string) => void;
  onOrgMenu?: (org: Org) => void;
  onContactEmail?: (a: Admin) => void;
  onContactCall?: (a: Admin) => void;
  onCreateSchool?: (payload: WizardPayload) => Promise<{ inviteLink?: string } | void> | { inviteLink?: string } | void;
  onAddEvent?: (payload: { title: string; date: string; school: string; notes: string }) => Promise<void> | void;
  onSearch?: (q: string) => void;
  onEditSchool?: (org: Org) => void;
  /** Archive an org: full snapshot, then hidden from lists. Reversible. */
  onArchiveOrg?: (org: Org) => Promise<void> | void;
  /** Permanently erase an org and every account attached to it. Omit to leave
      the console's delete button disabled — better than a button that lies. */
  onPurgeOrg?: (org: Org) => Promise<void> | void;
  /** Forget an org's learned + half-learned routes; learning restarts with the
      next trip. Trips themselves are kept. */
  onResetRouteLearning?: (org: Org) => Promise<{ deleted: number } | void> | void;
  /** Read-only: why has / hasn't a route learned (trips since reset + the
      clusters the learner forms). Bug 129. */
  onRouteLearningStatus?: (org: Org) => Promise<RouteLearningStatus>;
  /** Heal the trips the sealed-trail bug (135) left unlearnable, then rebuild
      this org's routes now. Returns what happened, for the readout. */
  onRouteLearningHeal?: (org: Org, trips: RouteLearningStatusTrip[]) => Promise<{ healed: number; skipped: number; rebuilt: number | null; snapOn?: boolean | null; snapped?: number; snapFailed?: number; snapError?: string | null; roadsNamed?: number; nameErrors?: number; nameError?: string | null; error?: string }>;
  /** Wipe an org's fuel data (events, station stops, engine state, fuel
      alerts, cached bus fields). Config, prices and trips are kept. */
  onResetFuelData?: (org: Org) => Promise<{ deleted: number; counts?: Record<string, number> } | void> | void;
  /** Backfill / edit an org's home location (geofencing anchor). */
  onSaveHomeLocation?: (orgCode: string, loc: { lat: number; lng: number }) => Promise<void> | void;
  /** Persist an add-on's credentials for an org (server-side). */
  onSaveConnection?: (orgCode: string, addon: AddonKey, creds: { provider: string; apiUrl: string; apiKey: string; secret: string }) => Promise<{ ok: boolean; message?: string }>;
  /** Live-verify an add-on connection. */
  onTestConnection?: (orgCode: string, addon: AddonKey, provider: string) => Promise<{ ok: boolean; message?: string; sampleCount?: number }>;
  /** Mark a PEAR-ops platform alert as reviewed. */
  onAcknowledgePlatformAlert?: (alertId: string) => Promise<void> | void;
  /** Override auto-applied press ceilings with official EPRA numbers. */
  onApplyManualEpraCeiling?: (args: {
    alertId: string;
    diesel: number;
    petrol: number;
  }) => Promise<{ ok?: boolean; refreshedOrgCount?: number }> | void;
}
export interface WizardPayload {
  kind: string; lorryMode: string; name: string; code: string; fullCode: string;
  email: string; phone: string; addr: string; plan: string; endDate: string; active: boolean;
  /** The admin's own name, shown in the apps instead of their email (Gabriel, 2026-10-27). */
  adminName: string;
  /** Hard caps so a beta org can't invite unlimited users. 0 = unlimited. */
  maxStudents: number; maxTeachers: number; maxParents: number;
  /** Required campus home coordinate (geofencing anchor). */
  homeLocations: { lat: number; lng: number } | null;
}

const TrasseConsole: React.FC<{ data?: ConsoleData; actions?: ConsoleActions }> = ({ data, actions }) => {
  const D = data || DEMO_DATA;
  const act = actions || {};

  /* ── ui state ── */
  const [loading, setLoading] = useState(true);
  const [paneLoading, setPaneLoading] = useState(false);
  const [tab, setTab] = useState<Tab>('dashboard');
  const [period, setPeriod] = useState<'month' | 'quarter' | 'year'>('month');
  const [orgFilter, setOrgFilter] = useState<'all' | 'active' | 'trial' | 'expired'>('all');
  const [query, setQuery] = useState('');
  const [dark, setDark] = useState(false);
  const [accent, setAccent] = useState('teal');
  const [density, setDensity] = useState<'compact' | 'regular'>('regular');
  const [fontSize, setFontSize] = useState(14);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [tweaksOpen, setTweaksOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [bellKey, setBellKey] = useState(0);
  const [modal, setModal] = useState<null | 'event' | 'wizard'>(null);
  const [toasts, setToasts] = useState<Toast[]>([]);
  const paneTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  /* ── org drilldown + API connection ── */
  const [orgView, setOrgView] = useState<string | null>(null);      // org code
  const [apiView, setApiView] = useState<AddonKey | null>(null);    // add-on id
  const [apiProvider, setApiProvider] = useState<string | null>(null);
  const [apiEnabled, setApiEnabled] = useState(true);
  const [apiBusy, setApiBusy] = useState(false);
  const [testLog, setTestLog] = useState<{ t: string; m: string }[]>([]);
  const [testResult, setTestResult] = useState<null | 'ok' | 'fail'>(null);
  const [testing, setTesting] = useState(false);
  const testTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  /* Deep-link from OrgSettingsPage: /admin?tab=organizations&org=CODE */
  useEffect(() => {
    const t = searchParams.get('tab');
    const org = searchParams.get('org');
    if (!t && !org) return;
    if (t === 'organizations' || org) {
      setLoading(false);
      setTab('organizations');
      setPaneLoading(false);
      setApiView(null);
      if (org) setOrgView(org);
      else setOrgView(null);
    }
    setSearchParams({}, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  /* ── wizard state ── */
  const [wStep, setWStep] = useState(1);
  const [wKind, setWKind] = useState<string>('FULL_FEATURE');
  const [wLorry, setWLorry] = useState<'SELF_TRACKING' | 'CARGO'>('SELF_TRACKING');
  const [wName, setWName] = useState('');
  const [wCode, setWCode] = useState('');
  const [wEmail, setWEmail] = useState('');
  const [wAdminName, setWAdminName] = useState('');
  const [wPhone, setWPhone] = useState('');
  const [wAddr, setWAddr] = useState('');
  const [wHome, setWHome] = useState<LatLng | null>(null);
  const [wPlan, setWPlan] = useState<string>('premium');
  const [wEndDate, setWEndDate] = useState('');
  const [wActive, setWActive] = useState(true);
  const [wMaxStudents, setWMaxStudents] = useState('500');
  const [wMaxTeachers, setWMaxTeachers] = useState('25');
  const [wMaxParents, setWMaxParents] = useState('1000');
  const [wDone, setWDone] = useState(false);
  /* selecting a plan prefills the caps with that plan's defaults (admin can tighten) */
  const pickPlan = (id: string) => {
    setWPlan(id);
    const pl = WPLANS.find(p => p.id === id);
    if (pl) { setWMaxStudents(String(pl.maxStudents)); setWMaxTeachers(String(pl.maxTeachers)); setWMaxParents(String(pl.maxParents)); }
  };
  const [wBusy, setWBusy] = useState(false);

  /* ── event modal state ── */
  const [evTitle, setEvTitle] = useState('');
  const [evDate, setEvDate] = useState('');
  const [evSchool, setEvSchool] = useState('');
  const [evNotes, setEvNotes] = useState('');

  const pushToast = (kind: ToastKind, title: string, msg: string) => {
    const id = Date.now() + Math.random();
    setToasts(t => [...t, { id, kind, title, msg }]);
    setTimeout(() => setToasts(t => t.filter(x => x.id !== id)), 4200);
  };

  /* initial load shimmer + welcome toast */
  useEffect(() => {
    const t1 = setTimeout(() => setLoading(false), 1100);
    const t2 = setTimeout(() => pushToast('success', 'Console ready', `Live data synced across ${D.orgs.length || 0} schools.`), 1500);
    return () => { clearTimeout(t1); clearTimeout(t2); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  /* reflect live loading flag once initial shimmer done */
  useEffect(() => { if (!D.loading) return; }, [D.loading]);

  const go = (t: Tab) => {
    setOrgView(null); setApiView(null);
    if (t === tab) { setSidebarOpen(false); return; }
    if (paneTimer.current) clearTimeout(paneTimer.current);
    setTab(t); setSidebarOpen(false); setPaneLoading(true);
    paneTimer.current = setTimeout(() => setPaneLoading(false), 480);
  };

  /* org drilldown handlers */
  const openOrg = (code: string) => { setOrgView(code); setApiView(null); setTestLog([]); setTestResult(null); };
  const backToOrgs = () => { setOrgView(null); setApiView(null); };
  const openApi = (addon: AddonKey, provider?: string | null) => { setApiView(addon); setApiProvider(provider || null); setApiEnabled(true); setTestLog([]); setTestResult(null); setTesting(false); };
  const backToOrg = () => { setApiView(null); setTestLog([]); setTestResult(null); setTesting(false); };
  const currentOrg = orgView ? (D.orgs.find(o => o.code === orgView) || null) : null;
  /* 2026-09-29: after "Delete school" the org left D.orgs but `orgView` still
     held its code, so neither the list (needs !orgView) nor the detail pane
     (needs currentOrg) rendered — a blank page until the sidebar was clicked
     (Gabriel: "the screen just went blank"). An org that has vanished from
     LIVE data sends the console back to the list; demo data and the loading
     window are excluded so a deep link is not bounced before its org arrives. */
  useEffect(() => {
    if (orgView && !currentOrg && data && !D.loading) backToOrgs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgView, currentOrg, data, D.loading]);
  const apiDef = apiView ? addonDef(apiView) : undefined;
  const apiConn: AddonConn | undefined = currentOrg && apiView ? currentOrg.conns?.[apiView] : undefined;
  const apiSelectedProvider = apiProvider || apiConn?.provider || apiDef?.providers[0] || '';

  const saveApi = async () => {
    if (!currentOrg || !apiView || !apiDef) return;
    setApiBusy(true);
    const endpoint = (document.getElementById('tc-api-endpoint') as HTMLInputElement)?.value || '';
    const key = (document.getElementById('tc-api-key') as HTMLInputElement)?.value || '';
    const secret = (document.getElementById('tc-api-secret') as HTMLInputElement)?.value || '';
    try {
      const res = act.onSaveConnection
        ? await act.onSaveConnection(currentOrg.code, apiView, { provider: apiSelectedProvider, apiUrl: endpoint, apiKey: key, secret })
        : { ok: true };
      if (res.ok) pushToast('success', 'Connection saved', `${apiSelectedProvider} credentials stored for ${currentOrg.name}.`);
      else pushToast('error', 'Could not save', res.message || 'Please try again.');
    } catch (e: any) { pushToast('error', 'Could not save', e?.message || 'Please try again.'); }
    finally { setApiBusy(false); }
  };

  const runTest = async () => {
    if (testing || !currentOrg || !apiView || !apiDef) return;
    if (testTimer.current) clearTimeout(testTimer.current);
    setTesting(true); setTestLog([]); setTestResult(null);
    const endpoint = (document.getElementById('tc-api-endpoint') as HTMLInputElement)?.value || `https://api.trasse.io/${apiView}`;
    const intro = [
      { t: 'cmd', m: `$ trasse test-connection --org ${currentOrg.code} --addon ${apiView}` },
      { t: 'muted', m: `Resolving provider · ${apiSelectedProvider}…` },
      { t: 'muted', m: `GET ${endpoint}/health` },
    ];
    // stream the intro lines, then the real (or simulated) verdict
    let i = 0;
    const pump = (extra: { t: string; m: string }[]) => {
      const lines = intro.concat(extra);
      const step = () => {
        setTestLog(prev => [...prev, lines[i]]);
        const last = i === lines.length - 1;
        if (last) { setTesting(false); setTestResult(lines[i].t === 'fail' ? 'fail' : (lines[i].t === 'done' ? 'ok' : 'fail')); return; }
        i++; testTimer.current = setTimeout(step, 480);
      };
      testTimer.current = setTimeout(step, 350);
    };
    if (!apiEnabled) {
      pump([{ t: 'err', m: 'Connection is disabled for this integration.' }, { t: 'fail', m: '✗ Enable the integration above, then run the test again.' }]);
      return;
    }
    if (apiDef.real && act.onTestConnection) {
      try {
        const res = await act.onTestConnection(currentOrg.code, apiView, apiSelectedProvider);
        if (res.ok) pump([
          { t: 'ok', m: '200 OK — handshake established' },
          { t: 'muted', m: 'Authenticating with stored API key…' },
          { t: 'ok', m: 'Auth accepted — scope: read' },
          { t: 'ok', m: `Received ${res.sampleCount || 0} records — schema valid` },
          { t: 'done', m: `✓ ${res.message || `${apiSelectedProvider} is live for ${currentOrg.name}`}` },
        ]);
        else pump([
          { t: 'err', m: res.message || 'Provider did not return valid data.' },
          { t: 'fail', m: '✗ Connection failed — check the credentials above.' },
        ]);
      } catch (e: any) {
        pump([{ t: 'err', m: e?.message || 'Verification failed.' }, { t: 'fail', m: '✗ Connection failed — try again.' }]);
      }
    } else {
      // sms / demo: simulated healthy handshake
      pump([
        { t: 'ok', m: '200 OK — handshake established (142 ms)' },
        { t: 'muted', m: 'Authenticating with API key…' },
        { t: 'ok', m: 'Auth accepted — scope: read, write' },
        { t: 'ok', m: 'Received 24 records — schema valid' },
        { t: 'done', m: `✓ Connection healthy — ${apiSelectedProvider} is live for ${currentOrg.name}` },
      ]);
    }
  };

  const openWizard = () => {
    setModal('wizard'); setWStep(1); setWDone(false); setWKind('FULL_FEATURE'); setWLorry('SELF_TRACKING');
    setWName(''); setWCode(''); setWEmail(''); setWAdminName(''); setWPhone(''); setWAddr(''); setWHome(null); setWActive(true); setWEndDate('');
    pickPlan('premium');
  };
  const closeModal = () => { setModal(null); setWStep(1); setWDone(false); };
  const primaryAction = () => { if (tab === 'calendar') { setModal('event'); } else openWizard(); };
  const logout = () => { pushToast('error', 'Signed out', 'Your session has ended. Redirecting…'); act.onLogout?.(); };

  const wSuffix = () => wKind === 'LORRY' ? (wLorry === 'CARGO' ? '.cargo' : '.fleet') : (wKind === 'BUS_TRACKING' ? '.bus' : '');
  const wFullCode = () => { const c = (wCode || '').toUpperCase().replace(/[^A-Z0-9]/g, ''); return (c || 'CODE') + wSuffix(); };
  const wCanNext = wStep === 2 ? Boolean(wName.trim() && (wCode || '').trim() && wAdminName.trim() && wEmail.trim() && wHome) : true;

  const submitEvent = async () => {
    setModal(null);
    await act.onAddEvent?.({ title: evTitle, date: evDate, school: evSchool, notes: evNotes });
    pushToast('success', 'Event added', 'The event is now on the shared calendar.');
    setEvTitle(''); setEvDate(''); setEvSchool(''); setEvNotes('');
  };
  const wCreate = async () => {
    setWBusy(true);
    try {
      const result = await act.onCreateSchool?.({ kind: wKind, lorryMode: wLorry, name: wName, code: wCode, fullCode: wFullCode(), email: wEmail, adminName: wAdminName, phone: wPhone, addr: wAddr, plan: wPlan, endDate: wEndDate, active: wActive, maxStudents: parseInt(wMaxStudents, 10) || 0, maxTeachers: parseInt(wMaxTeachers, 10) || 0, maxParents: parseInt(wMaxParents, 10) || 0, homeLocations: wHome });
      setWDone(true);
      const inviteLink = result && typeof result === 'object' ? result.inviteLink : undefined;
      if (inviteLink) {
        try { await navigator.clipboard.writeText(inviteLink); } catch { /* ignore */ }
        pushToast('success', 'School provisioned', `Invite sent to ${wEmail}. Backup link copied — paste in a browser if the email button hangs.`);
      } else {
        pushToast('success', 'School provisioned', `${wName || 'The school'} is live — invite sent to ${wEmail}.`);
      }
    } catch (e: any) {
      pushToast('error', 'Could not create', e?.message || 'Provisioning failed — please try again.');
    } finally { setWBusy(false); }
  };

  /* theme vars */
  const themeVars = useMemo(() => buildTheme(dark, accent), [dark, accent]);
  const rootStyle: CSS = { ...(themeVars as CSS), display: 'flex', minHeight: '100vh', background: 'var(--bg)', color: 'var(--text)', fontSize: `${fontSize}px`, lineHeight: 1.45 };

  const [pageTitle, pageSub] = TITLES[tab];
  const primaryLabels: Partial<Record<Tab, string>> = { dashboard: 'Add school', organizations: 'Add school', calendar: 'New event' };
  const primaryLabel = primaryLabels[tab] || 'Add school';
  const showPrimary = ['dashboard', 'organizations', 'calendar'].includes(tab);

  const skel = 'linear-gradient(90deg,var(--sunken) 25%,var(--soft) 50%,var(--sunken) 75%)';
  const skelStyle = (extra: CSS): CSS => ({ background: skel, backgroundSize: '840px 100%', animation: 'tc-shimmer 1.3s linear infinite', borderRadius: '8px', ...extra });
  const skelCard = (extra: CSS): CSS => ({ background: skel, backgroundSize: '840px 100%', animation: 'tc-shimmer 1.3s linear infinite', borderRadius: 'var(--r-lg)', ...extra });

  const periodBtn = (on: boolean): CSS => ({ border: 'none', background: on ? 'var(--sunken)' : 'transparent', color: on ? 'var(--text)' : 'var(--t3)', fontFamily: 'inherit', fontWeight: 600, fontSize: '13px', padding: '7px 13px', borderRadius: '8px', cursor: 'pointer' });
  const ftab = (on: boolean): CSS => ({ border: 'none', background: on ? 'var(--sunken)' : 'transparent', color: on ? 'var(--text)' : 'var(--t3)', fontFamily: 'inherit', fontWeight: 600, fontSize: '13px', padding: '7px 14px', borderRadius: '8px', cursor: 'pointer' });
  const thStyle: CSS = { fontSize: '11px', fontWeight: 700, color: 'var(--t4)', textTransform: 'uppercase', letterSpacing: '0.05em', padding: '11px 16px', background: 'var(--soft)' };
  const card: CSS = { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--r-lg)', boxShadow: 'var(--sh-xs)' };
  const cardPad: CSS = { ...card, padding: '20px 22px' };

  const contentStyle: CSS = { padding: density === 'compact' ? '18px 20px 60px' : '26px 28px 60px', maxWidth: '1360px', width: '100%', margin: '0 auto' };

  const mono = "'Geist Mono','Geist',ui-monospace,monospace";

  /* search + filtered data (search filters the active section) */
  const q = query.trim().toLowerCase();
  const has = (...vals: (string | undefined)[]) => !q || vals.some(v => (v || '').toLowerCase().includes(q));
  const orgsFiltered = D.orgs
    .filter(o => orgFilter === 'all' ? true : orgFilter === 'active' ? o.status === 'Active' : orgFilter === 'trial' ? o.status === 'Trial' : o.status === 'Expired')
    .filter(o => has(o.name, o.code, o.plan, o.status));
  const orgCountLabel = `${orgsFiltered.length} of ${D.orgs.length} organisations`;
  const adminGroupsF = q
    ? D.adminGroups.map(g => ({ ...g, admins: g.admins.filter(u => has(u.name, u.email, u.role) || (g.school || '').toLowerCase().includes(q)) })).filter(g => g.admins.length)
    : D.adminGroups;
  const busGroupsF = q
    ? D.busGroups.map(g => ({ ...g, buses: g.buses.filter(b => has(b.plate, b.driver, b.route) || (g.school || '').toLowerCase().includes(q)) })).filter(g => g.buses.length)
    : D.busGroups;
  const lorriesF = q ? D.lorries.filter(l => has(l.plate, l.model, l.cargo, l.status)) : D.lorries;

  /* Month / Quarter / Year.
     `D.revenue.points` is 12 monthly MRR figures (oldest → newest), so a
     period is a window over them: Month = the current month's MRR, Quarter =
     what the last three months brought in, Year = the last twelve. The chart
     aggregates to match, otherwise pressing Quarter would change a number
     while the graph underneath it stayed identical. */
  const revView = useMemo(() => {
    const pts = D.revenue.points;
    const labels = D.revenue.months;
    const sum = (a: number[]) => a.reduce((n, v) => n + v, 0);
    const money = (n: number) =>
      n >= 1e6 ? `${(n / 1e6).toFixed(2)}M` : n >= 1e3 ? `${Math.round(n / 1e3)}K` : String(Math.round(n));

    if (period === 'quarter') {
      const groups: number[][] = [];
      const groupLabels: string[] = [];
      for (let i = 0; i < pts.length; i += 3) {
        const slice = pts.slice(i, i + 3);
        if (!slice.length) continue;
        groups.push(slice);
        const from = labels[i] ?? '';
        const to = labels[Math.min(i + slice.length - 1, labels.length - 1)] ?? '';
        groupLabels.push(from === to ? from : `${from}–${to}`);
      }
      return {
        points: groups.map(sum),
        months: groupLabels,
        total: money(sum(pts.slice(-3))),
        caption: 'Last 3 months · KES',
      };
    }
    if (period === 'year') {
      return {
        points: pts,
        months: labels,
        total: money(sum(pts)),
        caption: 'Last 12 months · KES',
      };
    }
    return {
      points: pts,
      months: labels,
      total: D.revenue.total,
      caption: 'This month · KES',
    };
  }, [D.revenue, period]);

  const rev = revenuePaths(revView.points);
  const arcs = donutArcs(D.planMix.premium, D.planMix.standard, D.planMix.trial);

  /* nav counts from live data */
  const navCounts: Partial<Record<Tab, string>> = {
    organizations: String(D.orgs.length),
    users: String(D.adminGroups.reduce((n, g) => n + g.admins.length, 0)),
    bus: `${D.busGroups.reduce((n, g) => n + g.buses.filter(b => b.status === 'En route').length, 0)} live`,
  };

  const showInitialSkeleton = loading || D.loading;

  /* ══════════════ render ══════════════ */
  return (
    <div className="tc-root" style={rootStyle}>
      {/* scrim for mobile sidebar */}
      {sidebarOpen && (
        <div onClick={() => setSidebarOpen(false)} className="tc-fade" style={{ position: 'fixed', inset: 0, background: 'rgba(10,10,20,0.5)', backdropFilter: 'blur(2px)', zIndex: 60 }} />
      )}

      {/* ══ SIDEBAR ══ */}
      <aside className={`tc-sidebar${sidebarOpen ? ' open' : ''}`} style={{ width: '250px', flexShrink: 0, background: 'var(--nav-bg)', borderRight: '1px solid var(--nav-line)', display: 'flex', flexDirection: 'column', position: 'sticky', top: 0, height: '100vh', zIndex: 65, transition: 'transform 0.28s cubic-bezier(.2,.7,.2,1)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '11px', padding: '22px 20px 18px' }}>
          <div style={{ width: '32px', height: '32px', borderRadius: '9px', background: 'linear-gradient(140deg,var(--accent),var(--accent-strong))', display: 'grid', placeItems: 'center', flexShrink: 0, boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.25)' }}>
            <Ico d="M4 18 L10 10 L14 14 L20 6" size={17} sw={2.4} stroke="#fff" />
          </div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '7px' }}>
            <span style={{ fontSize: '18px', fontWeight: 800, letterSpacing: '-0.02em', color: '#fff' }}>trasse</span>
            <span style={{ fontSize: '9px', fontWeight: 700, letterSpacing: '0.1em', color: 'var(--accent)', background: 'color-mix(in oklab, var(--accent) 20%, transparent)', padding: '3px 6px', borderRadius: '5px' }}>CONSOLE</span>
          </div>
          <button onClick={() => setSidebarOpen(false)} className="tc-navclose" style={{ marginLeft: 'auto', background: 'none', border: 'none', color: 'var(--nav-muted)', cursor: 'pointer', padding: '4px' }}>
            <Ico d="M6 6l12 12M18 6L6 18" size={20} sw={2} />
          </button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '0 12px 12px', minHeight: 0 }}>
          {NAV.map(group => (
            <div key={group.label}>
              <div style={{ fontSize: '10px', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--nav-muted)', padding: '16px 10px 6px' }}>{group.label}</div>
              {group.items.map(it => {
                const on = it.id === tab;
                const count = navCounts[it.id];
                return (
                  <div key={it.id} onClick={() => go(it.id)} className={`tc-navitem${on ? ' on' : ''}`} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 12px', borderRadius: '10px', cursor: 'pointer', fontSize: '14px', fontWeight: 600, marginBottom: '2px', color: on ? '#fff' : 'var(--nav-text)', background: on ? 'color-mix(in oklab, var(--accent) 22%, transparent)' : 'transparent', boxShadow: on ? 'inset 3px 0 0 var(--accent)' : 'none' }}>
                    <Ico d={it.d} size={18} style={{ flexShrink: 0 }} />
                    <span style={{ flex: 1 }}>{it.label}</span>
                    {count ? (
                      <span style={{ marginLeft: 'auto', fontSize: '11px', fontWeight: 700, color: on ? 'var(--accent)' : 'var(--nav-muted)', background: on ? 'rgba(255,255,255,0.08)' : 'var(--nav-bg2)', padding: '2px 8px', borderRadius: '999px' }}>{count}</span>
                    ) : null}
                  </div>
                );
              })}
            </div>
          ))}
        </div>

        <div style={{ padding: '12px', borderTop: '1px solid var(--nav-line)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '11px', padding: '9px 10px', borderRadius: '12px', background: 'var(--nav-bg2)' }}>
            <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: 'linear-gradient(140deg,var(--accent),var(--accent-strong))', display: 'grid', placeItems: 'center', fontWeight: 700, color: '#fff', flexShrink: 0, fontSize: '14px' }}>{D.user.initials}</div>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontSize: '13px', fontWeight: 700, color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{D.user.name}</div>
              <div style={{ fontSize: '11px', color: 'var(--accent)', fontWeight: 600 }}>{D.user.role}</div>
            </div>
            <button onClick={logout} title="Log out" className="tc-logout" style={{ background: 'none', border: 'none', color: 'var(--nav-muted)', cursor: 'pointer', padding: '5px', borderRadius: '7px', display: 'grid', placeItems: 'center' }}>
              <Ico d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9" size={17} sw={2} />
            </button>
          </div>
        </div>
      </aside>

      {/* ══ MAIN ══ */}
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', background: 'var(--bg)' }}>
        {/* topbar */}
        <div style={{ height: '64px', flexShrink: 0, borderBottom: '1px solid var(--hairline)', display: 'flex', alignItems: 'center', gap: '14px', padding: '0 22px', position: 'sticky', top: 0, background: 'color-mix(in oklab, var(--bg) 88%, transparent)', backdropFilter: 'saturate(180%) blur(10px)', zIndex: 30 }}>
          <button onClick={() => setSidebarOpen(true)} className="tc-hamburger" style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '9px', width: '38px', height: '38px', placeItems: 'center', cursor: 'pointer', color: 'var(--t2)', flexShrink: 0 }}>
            <Ico d="M3 6h18M3 12h18M3 18h18" size={18} sw={2} />
          </button>
          <div style={{ fontSize: '16px', fontWeight: 700, letterSpacing: '-0.01em' }}>{pageTitle}</div>
          <div className="tc-search" style={{ display: 'flex', alignItems: 'center', gap: '8px', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '9px', padding: '8px 11px', width: '280px', color: 'var(--t3)', marginLeft: '12px' }}>
            <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth={2}><circle cx="11" cy="11" r="7" /><path d="M21 21l-4-4" /></svg>
            <input placeholder="Search schools, users, buses…" value={query} onChange={e => { setQuery(e.target.value); act.onSearch?.(e.target.value); }} style={{ background: 'transparent', border: 'none', outline: 'none', color: 'var(--text)', font: 'inherit', fontSize: '13px', width: '100%' }} />
            <kbd style={{ fontFamily: mono, fontSize: '11px', padding: '1px 5px', border: '1px solid var(--border)', borderRadius: '4px', color: 'var(--t3)' }}>/</kbd>
          </div>
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div className="tc-envpill" style={{ display: 'inline-flex', alignItems: 'center', gap: '7px', fontSize: '12px', fontWeight: 600, color: 'var(--green)', background: 'var(--green-soft)', padding: '6px 11px', borderRadius: '999px' }}>
              <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: 'var(--green)', boxShadow: '0 0 0 3px color-mix(in oklab,var(--green) 22%,transparent)' }} />
              Live
            </div>
            <button onClick={() => { setNotifOpen(o => !o); setBellKey(k => k + 1); }} className="tc-iconbtn" style={{ position: 'relative', width: '38px', height: '38px', borderRadius: '10px', display: 'grid', placeItems: 'center', background: 'var(--surface)', border: '1px solid var(--border)', cursor: 'pointer', color: 'var(--t2)' }}>
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth={1.9} strokeLinecap="round" strokeLinejoin="round" style={{ transformOrigin: '50% 4px', animation: bellKey ? 'tc-bell 0.8s ease' : 'none' }}><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9M13.7 21a2 2 0 0 1-3.4 0" /></svg>
              {D.notifs.length > 0 && <>
                <span style={{ position: 'absolute', top: '7px', right: '8px', width: '8px', height: '8px', borderRadius: '50%', background: 'var(--red)', border: '2px solid var(--surface)' }} />
                <span style={{ position: 'absolute', top: '8px', right: '9px', width: '6px', height: '6px', borderRadius: '50%', background: 'var(--red)', animation: 'tc-ping 2.2s ease-out infinite' }} />
              </>}
            </button>
            <button onClick={() => setTweaksOpen(o => !o)} className="tc-softbtn" style={{ display: 'inline-flex', alignItems: 'center', gap: '7px', fontWeight: 600, fontSize: '13px', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '10px', padding: '8px 13px', cursor: 'pointer', color: 'var(--text)', boxShadow: 'var(--sh-xs)' }}>
              <svg viewBox="0 0 24 24" width="15" height="15" fill="var(--accent)" stroke="none"><path d="M12 2l1.9 4.8L19 8l-3.6 3.4L16.4 17 12 14.3 7.6 17l1-5.6L5 8l5.1-1.2z" /></svg>
              Tweaks
            </button>
          </div>
        </div>

        {/* content */}
        <div style={contentStyle}>
          {showInitialSkeleton ? (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '24px' }}>
                <div>
                  <div style={skelStyle({ width: '220px', height: '26px', marginBottom: '10px' })} />
                  <div style={skelStyle({ width: '300px', height: '14px' })} />
                </div>
                <div style={skelStyle({ width: '150px', height: '40px' })} />
              </div>
              <div className="tc-grid-kpi" style={{ marginBottom: '18px' }}>
                {[0, 1, 2, 3].map(i => <div key={i} style={skelCard({ height: '118px' })} />)}
              </div>
              <div className="tc-grid-19">
                <div style={skelCard({ height: '320px' })} />
                <div style={skelCard({ height: '320px' })} />
              </div>
            </div>
          ) : (
            <div key={tab} className="tc-fade">
              {/* page head */}
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '18px', flexWrap: 'wrap', marginBottom: '22px' }}>
                <div>
                  <h1 style={{ margin: 0, fontSize: '25px', fontWeight: 800, letterSpacing: '-0.025em' }}>{pageTitle}</h1>
                  <div style={{ color: 'var(--t3)', fontSize: '13.5px', marginTop: '4px' }}>{pageSub}</div>
                </div>
                <div style={{ display: 'flex', gap: '9px', flexWrap: 'wrap' }}>
                  {/* Only on the dashboard: this switches the revenue figure and
                      its chart. It used to render on every tab, where it changed
                      nothing — a control that does nothing reads as a broken one. */}
                  {tab === 'dashboard' && (
                    <div style={{ display: 'flex', gap: '3px', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '10px', padding: '3px' }}>
                      <button onClick={() => setPeriod('month')} style={periodBtn(period === 'month')}>Month</button>
                      <button onClick={() => setPeriod('quarter')} style={periodBtn(period === 'quarter')}>Quarter</button>
                      <button onClick={() => setPeriod('year')} style={periodBtn(period === 'year')}>Year</button>
                    </div>
                  )}
                  {showPrimary && (
                    <button onClick={primaryAction} className="tc-accentbtn" style={{ display: 'inline-flex', alignItems: 'center', gap: '7px', fontWeight: 600, fontSize: '13px', background: 'var(--accent)', color: 'var(--accent-fg)', border: '1px solid var(--accent-strong)', borderRadius: '10px', padding: '9px 15px', cursor: 'pointer', boxShadow: 'var(--sh-xs)' }}>
                      <Ico d="M12 5v14M5 12h14" size={16} sw={2.2} />
                      {primaryLabel}
                    </button>
                  )}
                </div>
              </div>

              {paneLoading ? (
                <div>
                  <div className="tc-grid-kpi" style={{ marginBottom: '16px' }}>
                    {[0, 1, 2, 3].map(i => <div key={i} style={skelCard({ height: '104px' })} />)}
                  </div>
                  <div className="tc-grid-19">
                    <div style={skelCard({ height: '300px' })} />
                    <div style={skelCard({ height: '300px' })} />
                  </div>
                </div>
              ) : (
                <div>
                  {tab === 'dashboard' && <DashboardPane D={D} rev={rev} revView={revView} arcs={arcs} card={card} cardPad={cardPad} mono={mono} goOrgs={() => go('organizations')} openOrgFromDash={(code: string) => { go('organizations'); openOrg(code); }} onAcknowledgePlatformAlert={act.onAcknowledgePlatformAlert} onApplyManualEpraCeiling={act.onApplyManualEpraCeiling} />}
                  {/* Platform-wide Zana: the total bill, whether her deeper path
                      is actually on, and the only stop button that exists. */}
                  {tab === 'dashboard' && (
                    <div style={{ marginTop: 16 }}>
                      <ZanaPlatformPanel card={card} mono={mono} />
                    </div>
                  )}
                  {tab === 'organizations' && !orgView && (
                    <OrgsPane orgsFiltered={orgsFiltered} orgCountLabel={orgCountLabel} orgFilter={orgFilter} setOrgFilter={setOrgFilter} ftab={ftab} thStyle={thStyle} card={card} mono={mono} onOpen={(o: Org) => openOrg(o.code)} />
                  )}
                  {tab === 'organizations' && orgView && !apiView && currentOrg && (
                    <OrgDetailPane org={currentOrg} card={card} mono={mono} back={backToOrgs} openApi={openApi} onArchive={act.onArchiveOrg} onPurge={act.onPurgeOrg} onResetRouteLearning={act.onResetRouteLearning} onRouteLearningStatus={act.onRouteLearningStatus} onRouteLearningHeal={act.onRouteLearningHeal} onResetFuelData={act.onResetFuelData} onSaveHome={async (code: string, loc: LatLng) => {
                      if (act.onSaveHomeLocation) { await act.onSaveHomeLocation(code, loc); pushToast('success', currentOrg.name, 'Home location saved.'); }
                      else pushToast('info', currentOrg.name, 'Home location saving requires the live console.');
                    }} />
                  )}
                  {tab === 'organizations' && orgView && apiView && currentOrg && apiDef && (
                    <ApiConnectPane org={currentOrg} def={apiDef} conn={apiConn} mono={mono} selectedProvider={apiSelectedProvider} setProvider={setApiProvider} enabled={apiEnabled} toggleEnabled={() => setApiEnabled(e => !e)} testLog={testLog} testing={testing} testResult={testResult} onRunTest={runTest} onSave={saveApi} busy={apiBusy} backToOrgs={backToOrgs} backToOrg={backToOrg} />
                  )}
                  {tab === 'users' && <UsersPane groups={adminGroupsF} card={card} mono={mono} onEmail={(a: Admin) => (act.onContactEmail ? act.onContactEmail(a) : pushToast('info', 'Opening email', `Composing a message to ${a.email}`))} onCall={(a: Admin) => (act.onContactCall ? act.onContactCall(a) : pushToast('info', 'Calling', `${a.name} · ${a.phone}`))} />}
                  {tab === 'bus' && <BusPane groups={busGroupsF} card={card} mono={mono} />}
                  {tab === 'lorry' && <LorryPane D={{ ...D, lorries: lorriesF }} card={card} thStyle={thStyle} mono={mono} />}
                  {tab === 'calendar' && <CalendarPane D={D} card={card} mono={mono} />}
                  {tab === 'content' && <ContentPane D={D} card={card} mono={mono} />}
                  {tab === 'reports' && <ReportsPane card={card} onGen={(t: string) => (act.onGenerateReport ? act.onGenerateReport(t) : pushToast('success', 'Generating', `${t} is being prepared.`))} />}
                  {tab === 'analytics' && <AnalyticsPane D={D} card={card} cardPad={cardPad} mono={mono} />}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ══ NOTIFICATIONS ══ */}
      {notifOpen && (
        <div onClick={() => setNotifOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 70 }}>
          <div onClick={e => e.stopPropagation()} style={{ position: 'absolute', top: '60px', right: '20px', width: '340px', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--r-lg)', boxShadow: 'var(--sh-lg)', overflow: 'hidden', animation: 'tc-slidein 0.24s cubic-bezier(.2,.7,.2,1) both' }}>
            <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--hairline)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ fontWeight: 700, fontSize: '14px' }}>Notifications</div>
              <span style={{ fontSize: '11px', fontWeight: 700, color: 'var(--accent)', background: 'var(--accent-soft)', padding: '2px 8px', borderRadius: '999px' }}>{D.notifs.length} new</span>
            </div>
            <div>
              {/* Rows navigate — an org notification opens that org, a platform
                  one opens its tab. They always LOOKED clickable (rowhover +
                  the whole panel is a popover); until 2026-08-06 they did
                  nothing, which Gabriel read — fairly — as "notifications are
                  broken on the console" (bug 81's console half). */}
              {D.notifs.map((n, i) => (
                <div
                  key={i}
                  className="tc-rowhover"
                  onClick={() => {
                    setNotifOpen(false);
                    if (n.orgCode) { go('organizations'); openOrg(n.orgCode); return; }
                    if (n.tab) go(n.tab as Tab);
                  }}
                  style={{ display: 'flex', gap: '11px', padding: '13px 16px', borderBottom: '1px solid var(--hairline)', cursor: (n.orgCode || n.tab) ? 'pointer' : 'default' }}
                >
                  <span style={{ width: '30px', height: '30px', borderRadius: '9px', flexShrink: 0, display: 'grid', placeItems: 'center', color: n.c, background: `color-mix(in oklab,${n.c} 14%, transparent)` }}><Ico d={n.d} size={14} sw={2} /></span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '13px', fontWeight: 600 }}>{n.title}</div>
                    <div style={{ fontSize: '11.5px', color: 'var(--t4)', marginTop: '2px' }}>{n.time}</div>
                  </div>
                </div>
              ))}
            </div>
            <div style={{ padding: '11px 16px', textAlign: 'center' }}>
              {/* These three sources all live on the dashboard pane — say so
                  instead of promising a "view all" page that does not exist. */}
              <a onClick={() => { setNotifOpen(false); go('dashboard'); }} style={{ fontSize: '12.5px', color: 'var(--accent)', fontWeight: 600, cursor: 'pointer' }}>See these on the dashboard</a>
            </div>
          </div>
        </div>
      )}

      {/* ══ TWEAKS ══ */}
      {tweaksOpen && <TweaksPanel dark={dark} setDark={setDark} accent={accent} setAccent={setAccent} density={density} setDensity={setDensity} fontSize={fontSize} setFontSize={setFontSize} close={() => setTweaksOpen(false)} goDashboard={() => { go('dashboard'); setTweaksOpen(false); }} />}

      {/* ══ EVENT MODAL ══ */}
      {modal === 'event' && (
        <EventModal
          title={evTitle} setTitle={setEvTitle} date={evDate} setDate={setEvDate}
          school={evSchool} setSchool={setEvSchool} notes={evNotes} setNotes={setEvNotes}
          close={closeModal} submit={submitEvent}
        />
      )}

      {/* ══ WIZARD ══ */}
      {modal === 'wizard' && (
        <Wizard
          wStep={wStep} setWStep={setWStep} wKind={wKind} setWKind={setWKind} wLorry={wLorry} setWLorry={setWLorry}
          wName={wName} setWName={setWName} wCode={wCode} setWCode={setWCode} wEmail={wEmail} setWEmail={setWEmail} wAdminName={wAdminName} setWAdminName={setWAdminName}
          wPhone={wPhone} setWPhone={setWPhone} wAddr={wAddr} setWAddr={setWAddr} wHome={wHome} setWHome={setWHome} wPlan={wPlan} pickPlan={pickPlan}
          wEndDate={wEndDate} setWEndDate={setWEndDate} wActive={wActive} setWActive={setWActive}
          wMaxStudents={wMaxStudents} setWMaxStudents={setWMaxStudents} wMaxTeachers={wMaxTeachers} setWMaxTeachers={setWMaxTeachers} wMaxParents={wMaxParents} setWMaxParents={setWMaxParents}
          wDone={wDone} wBusy={wBusy} wCanNext={wCanNext} wFullCode={wFullCode()} mono={mono}
          close={closeModal} create={wCreate}
        />
      )}

      {/* ══ TOASTS ══ */}
      <div style={{ position: 'fixed', bottom: '22px', right: '22px', zIndex: 100, display: 'flex', flexDirection: 'column', gap: '10px', width: '340px', maxWidth: 'calc(100vw - 44px)' }}>
        {toasts.map(t => {
          const cfg = TOAST_CFG[t.kind];
          return (
            <div key={t.id} style={{ display: 'flex', gap: '11px', alignItems: 'flex-start', background: 'var(--surface)', border: '1px solid var(--border)', borderLeft: `3px solid ${cfg.c}`, borderRadius: 'var(--r)', padding: '13px 14px', boxShadow: 'var(--sh-md)', animation: 'tc-toastin 0.3s cubic-bezier(.2,.7,.2,1) both' }}>
              <span style={{ width: '26px', height: '26px', borderRadius: '8px', flexShrink: 0, display: 'grid', placeItems: 'center', color: cfg.c, background: `color-mix(in oklab,${cfg.c} 15%, transparent)` }}><Ico d={cfg.d} size={15} sw={2.2} /></span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text)' }}>{t.title}</div>
                <div style={{ fontSize: '12px', color: 'var(--t3)', marginTop: '1px' }}>{t.msg}</div>
              </div>
              <button onClick={() => setToasts(x => x.filter(y => y.id !== t.id))} className="tc-closebtn" style={{ background: 'none', border: 'none', color: 'var(--t4)', cursor: 'pointer', padding: '2px', flexShrink: 0 }}><Ico d="M6 6l12 12M18 6L6 18" size={16} sw={2} /></button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

/* ════════════════════ SECTION PANES ════════════════════ */

const H3: React.FC<{ children: React.ReactNode; style?: CSS }> = ({ children, style }) => (
  <h3 style={{ margin: 0, fontSize: '15px', fontWeight: 700, ...style }}>{children}</h3>
);

/* Critical PEAR-ops alerts (press fuel update, etc.). Auto-applied numbers
 * still need a human eyes-on — dismiss only after you've checked. */
const EpraManualApplyRow: React.FC<{
  alert: PlatformAlert;
  mono: string;
  onApply?: (args: { alertId: string; diesel: number; petrol: number }) => Promise<{ refreshedOrgCount?: number } | void>;
}> = ({ alert, mono, onApply }) => {
  const [diesel, setDiesel] = useState('');
  const [petrol, setPetrol] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    setDiesel('');
    setPetrol('');
    setMsg(null);
    setErr(null);
  }, [alert.id]);

  const handleApply = async () => {
    if (!onApply) return;
    const d = Number(diesel);
    const p = Number(petrol);
    if (!diesel.trim() || !petrol.trim() || !Number.isFinite(d) || !Number.isFinite(p)) {
      setErr('Enter official diesel and petrol KES/L from the EPRA notice.');
      return;
    }
    if (d < 50 || d > 500 || p < 50 || p > 500) {
      setErr('Each price must be between 50 and 500 KES/L.');
      return;
    }
    setBusy(true);
    setErr(null);
    setMsg(null);
    try {
      const out = await onApply({ alertId: alert.id, diesel: d, petrol: p });
      const n = out && typeof out === 'object' ? out.refreshedOrgCount : undefined;
      setMsg(
        typeof n === 'number'
          ? `Applied — diesel ${d} / petrol ${p} KES/L (${n} fleet${n === 1 ? '' : 's'} refreshed).`
          : `Applied — diesel ${d} / petrol ${p} KES/L.`,
      );
    } catch (e: any) {
      setErr(e?.message || 'Could not apply ceilings.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ marginTop: '12px', padding: '12px', borderRadius: '10px', background: 'color-mix(in oklab,var(--accent) 6%, transparent)', border: '1px solid var(--hairline)' }}>
      <div style={{ fontSize: '12px', fontWeight: 700, color: 'var(--t2)', marginBottom: '8px' }}>
        Enter official EPRA Nairobi caps (KES/L)
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'center' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: 'var(--t3)' }}>
          Diesel
          <input
            type="number"
            step="0.01"
            min="50"
            max="500"
            value={diesel}
            placeholder={alert.caps?.diesel != null ? String(alert.caps.diesel) : '217.86'}
            onChange={(e) => setDiesel(e.target.value)}
            style={{ width: '92px', fontFamily: mono, fontSize: '12.5px', padding: '6px 8px', borderRadius: '8px', border: '1px solid var(--border)' }}
          />
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: 'var(--t3)' }}>
          Petrol
          <input
            type="number"
            step="0.01"
            min="50"
            max="500"
            value={petrol}
            placeholder={alert.caps?.petrol != null ? String(alert.caps.petrol) : '214.03'}
            onChange={(e) => setPetrol(e.target.value)}
            style={{ width: '92px', fontFamily: mono, fontSize: '12.5px', padding: '6px 8px', borderRadius: '8px', border: '1px solid var(--border)' }}
          />
        </label>
        <button
          type="button"
          disabled={busy || !onApply}
          onClick={() => void handleApply()}
          style={{ fontSize: '12.5px', fontWeight: 700, color: '#fff', background: 'var(--accent)', border: 'none', borderRadius: '8px', padding: '7px 14px', cursor: busy ? 'wait' : 'pointer', opacity: busy ? 0.7 : 1 }}
        >
          {busy ? 'Applying…' : 'Apply ceilings'}
        </button>
      </div>
      {err && <div style={{ fontSize: '12px', color: 'var(--red)', marginTop: '8px' }}>{err}</div>}
      {msg && <div style={{ fontSize: '12px', color: 'var(--green, #3da233)', marginTop: '8px' }}>{msg}</div>}
    </div>
  );
};

const PlatformAlertsCard: React.FC<any> = ({ rows, card, mono, onAcknowledge, onApplyManualEpraCeiling }) => {
  if (!rows || !rows.length) return null;
  return (
    <div style={{ ...card, padding: '17px 19px', marginBottom: '16px', borderColor: 'color-mix(in oklab,var(--red) 45%, var(--border))' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
        <span style={{ width: '28px', height: '28px', borderRadius: '8px', display: 'grid', placeItems: 'center', color: 'var(--red)', background: 'color-mix(in oklab,var(--red) 15%, transparent)' }}>
          <Ico d="M12 9v4M12 17h.01M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" size={15} sw={2} />
        </span>
        <H3>Critical — review required</H3>
        <span style={{ marginLeft: 'auto', fontSize: '12.5px', color: 'var(--red)', fontWeight: 700 }}>
          {rows.length} open
        </span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        {rows.map((a: PlatformAlert) => (
          <div key={a.id} style={{ padding: '12px 0', borderTop: '1px solid var(--hairline)' }}>
            <div style={{ fontSize: '13.5px', fontWeight: 700 }}>{a.title}</div>
            <div style={{ fontSize: '12.5px', color: 'var(--t3)', marginTop: '4px' }}>{a.message}</div>
            {a.caps && (
              <div style={{ fontSize: '12.5px', fontFamily: mono, marginTop: '8px', color: 'var(--t2)' }}>
                Diesel {a.caps.diesel ?? '—'} · Petrol {a.caps.petrol ?? '—'}
              </div>
            )}
            {Array.isArray(a.sources) && a.sources.length > 0 && (
              <div style={{ fontSize: '11.5px', color: 'var(--t4)', marginTop: '6px' }}>
                Sources: {a.sources.map(s => s.name).join(', ')}
              </div>
            )}
            {a.kind === 'epra_press_update' && (
              <EpraManualApplyRow alert={a} mono={mono} onApply={onApplyManualEpraCeiling} />
            )}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '10px' }}>
              <button
                type="button"
                onClick={() => onAcknowledge && onAcknowledge(a.id)}
                style={{ fontSize: '12.5px', fontWeight: 700, color: 'var(--accent)', background: 'transparent', border: '1px solid var(--border)', borderRadius: '8px', padding: '6px 12px', cursor: 'pointer' }}
              >
                Mark reviewed
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

/* Schools whose fuel costs are being worked out from an estimate.
 *
 * Rendered only when the list is non-empty, so a clean month leaves the
 * dashboard exactly as it was — an always-present card reading "0 schools"
 * teaches you to scroll past the place the warning will appear. */
const EstimatedFuelPricesCard: React.FC<any> = ({ rows, card, mono, openOrgFromDash }) => {
  if (!rows || !rows.length) return null;
  const promised = rows.filter((r: EstimatedPriceOrg) => r.promisedEpra);
  const tone = promised.length ? 'var(--red)' : 'var(--amber)';
  return (
    <div style={{ ...card, padding: '17px 19px', marginBottom: '16px', borderColor: `color-mix(in oklab,${tone} 35%, var(--border))` }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
        <span style={{ width: '28px', height: '28px', borderRadius: '8px', display: 'grid', placeItems: 'center', color: tone, background: `color-mix(in oklab,${tone} 15%, transparent)` }}>
          <Ico d="M14 3v6l4 2v8a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2zM4 9h10" size={15} sw={2} />
        </span>
        <H3>Fuel prices need attention</H3>
        <span style={{ marginLeft: 'auto', fontSize: '12.5px', color: 'var(--t3)' }}>
          {rows.length} school{rows.length === 1 ? '' : 's'} this month
        </span>
      </div>
      <div style={{ fontSize: '12.5px', color: 'var(--t3)', marginBottom: '10px' }}>
        Refuel costs and overspend detection for these fleets are running on an estimate,
        not a live EPRA ceiling or their own station prices.
      </div>
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        {rows.map((r: EstimatedPriceOrg) => (
          <div
            key={r.code}
            onClick={() => openOrgFromDash && openOrgFromDash(r.code)}
            className="tc-rowhover"
            style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 0', borderTop: '1px solid var(--hairline)', cursor: 'pointer' }}
          >
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontSize: '13px', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.name}</div>
              <div style={{ fontSize: '11.5px', color: 'var(--t4)' }}>{r.detail}</div>
            </div>
            <span style={{ fontSize: '11px', fontWeight: 700, fontFamily: mono, color: r.promisedEpra ? 'var(--red)' : 'var(--t4)', flexShrink: 0 }}>
              {r.promisedEpra ? 'EPRA' : r.plan}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

/* Standalone entry for official EPRA caps. The manual-apply row first shipped
   INSIDE the critical "review" alert card — so Mark reviewed dismissed the
   card and took the only way to type official numbers with it (Gabriel,
   2026-08-22: "I pressed mark reviewed by accident before testing"). This
   card is always on the dashboard; an empty alertId means nothing to annotate. */
const EpraManualCapsCard: React.FC<{
  card: React.CSSProperties;
  mono: string;
  latest?: PlatformAlert | null;
  onApply?: (args: { alertId: string; diesel: number; petrol: number }) => Promise<{ refreshedOrgCount?: number } | void>;
}> = ({ card, mono, latest, onApply }) => {
  const caps = latest?.caps ?? null;
  const stub: PlatformAlert = {
    id: '',
    kind: 'manual_epra_caps',
    severity: 'info',
    title: 'Official EPRA Nairobi caps',
    message: '',
    caps,
    acknowledged: true,
  };
  return (
    <div style={{ ...card, padding: '16px 18px', marginBottom: '16px' }}>
      <div style={{ fontSize: '14px', fontWeight: 700 }}>Official EPRA Nairobi caps</div>
      <div style={{ fontSize: '12.5px', color: 'var(--t3)', marginTop: '2px', lineHeight: 1.5 }}>
        Type the diesel and petrol ceilings from EPRA's notice here any time — they go to every Premium fleet. The critical "review" card above is only one way in; marking it reviewed no longer locks you out.
        {caps && (caps.diesel != null || caps.petrol != null) ? ` Press consensus last applied diesel ${caps.diesel ?? '—'} / petrol ${caps.petrol ?? '—'}.` : ''}
      </div>
      <EpraManualApplyRow alert={stub} mono={mono} onApply={onApply} />
    </div>
  );
};

const DashboardPane: React.FC<any> = ({ D, rev, revView, arcs, card, cardPad, mono, goOrgs, openOrgFromDash, onAcknowledgePlatformAlert, onApplyManualEpraCeiling }) => (
  <div>
    <PlatformAlertsCard rows={D.platformAlerts} card={card} mono={mono} onAcknowledge={onAcknowledgePlatformAlert} onApplyManualEpraCeiling={onApplyManualEpraCeiling} />
    <EpraManualCapsCard card={card} mono={mono} latest={(D.platformAlerts || []).find((a: PlatformAlert) => a.kind === 'epra_press_update') ?? null} onApply={onApplyManualEpraCeiling} />
    <EstimatedFuelPricesCard rows={D.estimatedPriceOrgs} card={card} mono={mono} openOrgFromDash={openOrgFromDash} />
    <div className="tc-grid-kpi" style={{ marginBottom: '16px' }}>
      {D.kpis.map((k: any, i: number) => (
        <div key={i} style={{ ...card, padding: '17px 19px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '9px', fontSize: '12.5px', color: 'var(--t3)', fontWeight: 600 }}>
            <span style={{ width: '28px', height: '28px', borderRadius: '8px', background: 'var(--accent-soft)', display: 'grid', placeItems: 'center', color: 'var(--accent)' }}><Ico d={k.d} size={15} /></span>
            {k.label}
          </div>
          <div style={{ fontSize: '28px', fontWeight: 800, letterSpacing: '-0.02em', marginTop: '13px', lineHeight: 1, fontFamily: mono }}>{k.value}</div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '12px' }}>
            <span style={{ fontSize: '12.5px', fontWeight: 700, color: k.up ? 'var(--green)' : 'var(--red)' }}>{k.delta}</span>
            <span style={{ fontSize: '11.5px', color: 'var(--t4)' }}>{k.note}</span>
          </div>
        </div>
      ))}
    </div>

    <div className="tc-grid-19" style={{ marginBottom: '16px' }}>
      {/* revenue */}
      <div style={cardPad}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '6px' }}>
          <div>
            <H3>Revenue</H3>
            <div style={{ fontSize: '12.5px', color: 'var(--t3)', marginTop: '2px' }}>Collected across all schools · {revView.caption}</div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: '24px', fontWeight: 800, fontFamily: mono, letterSpacing: '-0.02em' }}>{revView.total}</div>
            <div style={{ fontSize: '12px', color: D.revenue.up ? 'var(--green)' : 'var(--red)', fontWeight: 700 }}>{D.revenue.deltaLabel}</div>
          </div>
        </div>
        <svg viewBox="0 0 720 240" preserveAspectRatio="none" style={{ width: '100%', height: '230px', marginTop: '8px' }}>
          {[40, 100, 160].map(y => <line key={y} x1="0" y1={y} x2="720" y2={y} stroke="var(--hairline)" strokeWidth={1} />)}
          <line x1="0" y1="200" x2="720" y2="200" stroke="var(--border)" strokeWidth={1} />
          <path d={rev.area} fill="var(--accent)" opacity={0.09} />
          <path d={rev.line} fill="none" stroke="var(--accent)" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" />
          <circle cx={rev.lastX} cy={rev.lastY} r={4.5} fill="var(--accent)" stroke="var(--surface)" strokeWidth={2} />
        </svg>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10.5px', color: 'var(--t4)', fontFamily: mono, marginTop: '2px' }}>
          {revView.months.map((m: string) => <span key={m}>{m}</span>)}
        </div>
      </div>

      {/* plan donut */}
      <div style={cardPad}>
        <H3 style={{ marginBottom: '2px' }}>Plan mix</H3>
        <div style={{ fontSize: '12.5px', color: 'var(--t3)' }}>{D.planMix.activeSchools} active schools</div>
        <div style={{ display: 'grid', placeItems: 'center', margin: '14px 0 6px' }}>
          <svg viewBox="0 0 120 120" width="150" height="150" style={{ transform: 'rotate(-90deg)' }}>
            <circle cx="60" cy="60" r="48" fill="none" stroke="var(--sunken)" strokeWidth={16} />
            {arcs.map((a: any, i: number) => (
              <circle key={i} cx="60" cy="60" r="48" fill="none" strokeWidth={16} strokeLinecap="round" stroke={a.stroke} strokeDasharray={a.strokeDasharray} strokeDashoffset={a.strokeDashoffset} />
            ))}
          </svg>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '9px' }}>
          {[['Premium', 'var(--accent)', D.planMix.premium], ['Standard', 'var(--info)', D.planMix.standard], ['Trial', 'var(--amber)', D.planMix.trial]].map(([label, c, v]: any) => (
            <div key={label} style={{ display: 'flex', alignItems: 'center', gap: '9px', fontSize: '12.5px' }}>
              <i style={{ width: '9px', height: '9px', borderRadius: '3px', background: c }} />
              <span style={{ flex: 1, color: 'var(--t2)' }}>{label}</span>
              <b style={{ fontFamily: mono }}>{v}</b>
            </div>
          ))}
        </div>
      </div>
    </div>

    <div className="tc-grid-13">
      {/* per-school revenue */}
      <div style={cardPad}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
          <H3>Revenue by school</H3>
          <a onClick={goOrgs} style={{ fontSize: '12.5px', color: 'var(--accent)', fontWeight: 600, cursor: 'pointer' }}>View all →</a>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
          {D.schoolRev.map((s: any, i: number) => (
            <div key={i}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '13px', marginBottom: '6px' }}>
                <span style={{ fontWeight: 600 }}>{s.name}</span>
                <span style={{ fontFamily: mono, color: 'var(--t2)', fontWeight: 600 }}>{s.amt}</span>
              </div>
              <div style={{ height: '8px', borderRadius: '999px', background: 'var(--sunken)', overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${s.pct}%`, borderRadius: '999px', background: 'linear-gradient(90deg,var(--accent),var(--accent-strong))' }} />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* recent activity */}
      <div style={cardPad}>
        <H3 style={{ marginBottom: '14px' }}>Recent activity</H3>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {D.activity.map((a: any, i: number) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '11px 0', borderBottom: '1px solid var(--hairline)' }}>
              <span style={{ width: '34px', height: '34px', borderRadius: '10px', display: 'grid', placeItems: 'center', flexShrink: 0, color: a.c, background: `color-mix(in oklab,${a.c} 15%, transparent)` }}><Ico d={a.d} size={15} sw={2} /></span>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontSize: '13px', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{a.title}</div>
                <div style={{ fontSize: '11.5px', color: 'var(--t4)' }}>{a.time}</div>
              </div>
              <span style={{ fontSize: '13px', fontWeight: 700, fontFamily: mono, color: a.amt ? (a.pos ? 'var(--green)' : 'var(--red)') : 'transparent' }}>{a.amt}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
);

type OrgSectionId = 'schools' | 'families' | 'fleet';

/** Bucket an org into Schools / Families / Fleet·cargo for the Organizations list. */
function orgSectionOf(o: Org): OrgSectionId {
  const code = (o.code || '').toLowerCase();
  if (isFamilyOrg(o.productType) || code.endsWith('.family')) return 'families';
  if (isLorryOrg(o.productType) || code.endsWith('.cargo') || code.endsWith('.fleet') || code.endsWith('.lorry')) return 'fleet';
  return 'schools';
}

const ORG_SECTIONS: { id: OrgSectionId; title: string; hint: string }[] = [
  { id: 'schools', title: 'Schools', hint: 'Trasse for schools' },
  { id: 'families', title: 'Families', hint: 'Trasse Families' },
  { id: 'fleet', title: 'Fleet / cargo', hint: 'Fleet and cargo owners' },
];

const OrgsPane: React.FC<any> = ({ orgsFiltered, orgCountLabel, orgFilter, setOrgFilter, ftab, thStyle, card, mono, onOpen }) => {
  const [open, setOpen] = useState<Record<OrgSectionId, boolean>>({ schools: true, families: true, fleet: true });
  const toggle = (id: OrgSectionId) => setOpen(prev => ({ ...prev, [id]: !prev[id] }));

  const bySection = useMemo(() => {
    const buckets: Record<OrgSectionId, Org[]> = { schools: [], families: [], fleet: [] };
    for (const o of orgsFiltered as Org[]) buckets[orgSectionOf(o)].push(o);
    return buckets;
  }, [orgsFiltered]);

  return (
    <div>
      <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: '3px', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '10px', padding: '3px' }}>
          {(['all', 'active', 'trial', 'expired'] as const).map(f => (
            <button key={f} onClick={() => setOrgFilter(f)} style={ftab(orgFilter === f)}>{f[0].toUpperCase() + f.slice(1)}</button>
          ))}
        </div>
        <span style={{ marginLeft: 'auto', fontSize: '12.5px', color: 'var(--t3)' }}>{orgCountLabel}</span>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
        {ORG_SECTIONS.map(sec => {
          const rows = bySection[sec.id];
          const isOpen = open[sec.id];
          return (
            <div key={sec.id} style={{ ...card, overflow: 'hidden' }}>
              <button
                type="button"
                onClick={() => toggle(sec.id)}
                aria-expanded={isOpen}
                style={{
                  display: 'flex', alignItems: 'center', gap: '11px', width: '100%',
                  padding: '14px 18px', border: 'none', background: 'var(--soft)',
                  borderBottom: isOpen ? '1px solid var(--hairline)' : 'none',
                  cursor: 'pointer', fontFamily: 'inherit', color: 'inherit', textAlign: 'left',
                }}
              >
                <span style={{
                  display: 'inline-grid', placeItems: 'center', width: '22px', height: '22px',
                  borderRadius: '6px', color: 'var(--t3)',
                  transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)',
                  transition: 'transform 0.15s ease',
                }}>
                  <Ico d="M9 18l6-6-6-6" size={15} sw={2} />
                </span>
                <span style={{ fontWeight: 700, fontSize: '14px', color: 'var(--text)' }}>{sec.title}</span>
                <span style={{ fontSize: '11.5px', color: 'var(--t4)' }}>{sec.hint}</span>
                <span style={{ marginLeft: 'auto', fontSize: '12px', fontWeight: 600, color: 'var(--t3)', fontFamily: mono }}>
                  {rows.length}
                </span>
              </button>

              {isOpen && (
                rows.length === 0 ? (
                  <div style={{ padding: '18px 18px', fontSize: '13px', color: 'var(--t4)' }}>
                    No {sec.title.toLowerCase()} match this filter.
                  </div>
                ) : (
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13.5px', minWidth: '720px' }}>
                      <thead>
                        <tr>
                          <th style={{ ...thStyle, textAlign: 'left' }}>School</th>
                          <th style={{ ...thStyle, textAlign: 'left' }}>Plan</th>
                          <th style={{ ...thStyle, textAlign: 'left' }}>Status</th>
                          <th style={{ ...thStyle, textAlign: 'right' }}>Students</th>
                          <th style={{ ...thStyle, textAlign: 'right' }}>Buses</th>
                          <th style={{ ...thStyle, textAlign: 'right' }}>MRR</th>
                          <th style={{ ...thStyle, textAlign: 'right' }}>Zana</th>
                          <th style={{ ...thStyle, textAlign: 'right' }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((o, i) => (
                          <tr key={o.code || i} onClick={() => onOpen(o)} className="tc-rowhover" style={{ borderTop: '1px solid var(--hairline)', cursor: 'pointer' }}>
                            <td style={{ padding: '13px 16px' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '11px' }}>
                                <span style={{ width: '34px', height: '34px', borderRadius: '10px', display: 'grid', placeItems: 'center', fontSize: '12px', fontWeight: 700, color: '#fff', flexShrink: 0, background: AV_COLORS[i % AV_COLORS.length] }}>{initialsOf(o.name)}</span>
                                <div>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
                                    <span style={{ fontWeight: 700, color: 'var(--text)' }}>{o.name}</span>
                                    {o.beta && <span style={{ fontSize: '9.5px', fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--accent)', background: 'var(--accent-soft)', padding: '2px 6px', borderRadius: '5px' }}>Beta</span>}
                                  </div>
                                  <div style={{ fontSize: '11.5px', color: 'var(--t4)', fontFamily: mono }}>{o.code}</div>
                                </div>
                              </div>
                            </td>
                            <td style={{ padding: '13px 16px' }}>
                              <span style={{ fontSize: '11.5px', fontWeight: 700, padding: '3px 9px', borderRadius: '7px', background: 'var(--soft)', border: '1px solid var(--border)' }}>{o.plan}</span>
                            </td>
                            <td style={{ padding: '13px 16px' }}>
                              <span style={orgStatusChip(o.status)}><i style={{ width: '6px', height: '6px', borderRadius: '50%', background: 'currentColor' }} />{o.status}</span>
                            </td>
                            <td style={{ padding: '13px 16px', textAlign: 'right', fontFamily: mono, color: 'var(--text)', fontWeight: 600 }}>{o.students}</td>
                            <td style={{ padding: '13px 16px', textAlign: 'right', fontFamily: mono, color: 'var(--text)', fontWeight: 600 }}>{o.buses}</td>
                            <td style={{ padding: '13px 16px', textAlign: 'right', fontFamily: mono, color: 'var(--text)', fontWeight: 700 }}>{o.mrr}</td>
                            <td style={{ padding: '13px 16px', textAlign: 'right', fontFamily: mono, color: 'var(--t2)', fontWeight: 600, fontSize: '12.5px' }}>
                              {o.zanaCapUsd == null ? '$15' : `$${Number(o.zanaCapUsd)}`}
                              <span style={{ display: 'block', fontSize: '10.5px', fontWeight: 600, color: 'var(--t4)' }}>{o.zanaCapUsd == null ? 'default' : '/mo'}</span>
                            </td>
                            <td style={{ padding: '13px 16px', textAlign: 'right' }}>
                              <span style={{ display: 'inline-grid', placeItems: 'center', width: '32px', height: '30px', borderRadius: '8px', color: 'var(--t4)' }}>
                                <Ico d="M9 18l6-6-6-6" size={16} sw={2} />
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const GroupHeader: React.FC<{ school: string; tag: string; right: string; mono: string }> = ({ school, tag, right, mono }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '11px', padding: '14px 18px', borderBottom: '1px solid var(--hairline)', background: 'var(--soft)' }}>
    <Ico d="M12 3 2 8l10 5 10-5zM2 16l10 5 10-5" size={16} stroke="var(--t3)" />
    <span style={{ fontWeight: 700, fontSize: '14px' }}>{school}</span>
    <span style={{ fontSize: '11.5px', color: 'var(--t4)', fontFamily: mono }}>{tag}</span>
    <span style={{ marginLeft: 'auto', fontSize: '11.5px', color: 'var(--t3)' }}>{right}</span>
  </div>
);

const UsersPane: React.FC<any> = ({ groups, card, mono, onEmail, onCall }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
    {groups.map((g: any, gi: number) => (
      <div key={gi} style={{ ...card, overflow: 'hidden' }}>
        <GroupHeader school={g.school} tag={g.tag} right={`${g.admins.length} ${g.admins.length === 1 ? 'admin' : 'admins'}`} mono={mono} />
        {g.admins.map((u: Admin, i: number) => (
          <div key={i} className="tc-rowhover" style={{ display: 'flex', alignItems: 'center', gap: '13px', padding: '13px 18px', borderBottom: '1px solid var(--hairline)', flexWrap: 'wrap' }}>
            <span style={{ width: '38px', height: '38px', borderRadius: '50%', display: 'grid', placeItems: 'center', fontSize: '13px', fontWeight: 700, color: '#fff', flexShrink: 0, background: AV_COLORS[(gi + i) % AV_COLORS.length] }}>{initialsOf(u.name)}</span>
            <div style={{ minWidth: '140px' }}>
              <div style={{ fontWeight: 700, fontSize: '13.5px' }}>{u.name}</div>
              <div style={{ fontSize: '12px', color: 'var(--t3)' }}>{u.email}</div>
            </div>
            <span style={roleChip(u.role)}>{u.role}</span>
            <span style={adminStatusChip(u.status)}><i style={{ width: '6px', height: '6px', borderRadius: '50%', background: 'currentColor' }} />{u.status}</span>
            <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px' }}>
              <a href={`mailto:${u.email}`} onClick={() => onEmail(u)} title="Email" className="tc-contact" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '12.5px', fontWeight: 600, color: 'var(--t2)', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '9px', padding: '7px 12px', cursor: 'pointer' }}>
                <Ico d="M4 4h16v16H4zM4 6l8 6 8-6" size={15} />Email
              </a>
              <a href={`tel:${u.phone}`} onClick={() => onCall(u)} title="Call" className="tc-contact" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '12.5px', fontWeight: 600, color: 'var(--t2)', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '9px', padding: '7px 12px', cursor: 'pointer' }}>
                <Ico d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3 19.5 19.5 0 0 1-6-6 19.8 19.8 0 0 1-3-8.6A2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1 1 .4 1.9.7 2.8a2 2 0 0 1-.5 2.1L8.1 9.9a16 16 0 0 0 6 6l1.3-1.3a2 2 0 0 1 2.1-.5c.9.3 1.8.6 2.8.7a2 2 0 0 1 1.7 2z" size={15} />Call
              </a>
            </div>
          </div>
        ))}
      </div>
    ))}
  </div>
);

const BusPane: React.FC<any> = ({ groups, card, mono }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
    {groups.map((g: any, gi: number) => {
      const enRoute = g.buses.filter((b: Bus) => b.status === 'En route').length;
      return (
        <div key={gi} style={{ ...card, overflow: 'hidden' }}>
          <GroupHeader school={g.school} tag={g.code} right={`${g.buses.length} buses · ${enRoute} en route`} mono={mono} />
          {g.buses.map((b: Bus, i: number) => (
            <div key={i} className="tc-rowhover" style={{ display: 'flex', alignItems: 'center', gap: '14px', padding: '13px 18px', borderBottom: '1px solid var(--hairline)', flexWrap: 'wrap' }}>
              <span style={{ width: '38px', height: '38px', borderRadius: '10px', background: 'var(--accent-soft)', display: 'grid', placeItems: 'center', color: 'var(--accent)', flexShrink: 0 }}>
                <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor"><path d="M4 16V6a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v10a1 1 0 0 1-1 1h-1a2 2 0 1 1-4 0H8a2 2 0 1 1-4 0H3a1 1 0 0 1-1-1Zm2-9v4h12V7Z" /></svg>
              </span>
              <div style={{ minWidth: '150px' }}>
                <div style={{ fontWeight: 700, fontSize: '13.5px', fontFamily: mono }}>{b.plate}</div>
                <div style={{ fontSize: '11.5px', color: 'var(--t4)' }}>{b.route}</div>
              </div>
              <div style={{ minWidth: '130px' }}>
                <div style={{ fontSize: '10.5px', color: 'var(--t4)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Driver</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '2px' }}>
                  <span style={{ fontSize: '12.5px', fontWeight: 600 }}>{b.driver}</span>
                  {b.rating > 0 && <span style={{ display: 'inline-flex', gap: '1px' }}>{starsFor(b.rating).map((f, k) => <Star key={k} fill={f} />)}</span>}
                  <span style={{ fontSize: '11px', color: 'var(--t3)', fontFamily: mono }}>{b.rating ? b.rating.toFixed(1) : '—'}</span>
                </div>
              </div>
              <div>
                <div style={{ fontSize: '10.5px', color: 'var(--t4)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Onboard</div>
                <div style={{ fontSize: '12.5px', fontWeight: 600, marginTop: '2px', fontFamily: mono }}>{b.kids}</div>
              </div>
              <span style={{ marginLeft: 'auto', ...busStatusChip(b.c) }}><i style={{ width: '6px', height: '6px', borderRadius: '50%', background: 'currentColor' }} />{b.status}</span>
            </div>
          ))}
        </div>
      );
    })}
  </div>
);

const LorryPane: React.FC<any> = ({ D, card, thStyle, mono }) => (
  <div>
    <div className="tc-grid-kpi" style={{ marginBottom: '18px' }}>
      {D.lorryMiniStats.map((m: any, i: number) => (
        <div key={i} style={{ ...card, borderRadius: 'var(--r)', padding: '15px 17px' }}>
          <div style={{ fontSize: '12px', color: 'var(--t3)', fontWeight: 600 }}>{m.k}</div>
          <div style={{ fontSize: '22px', fontWeight: 800, fontFamily: mono, marginTop: '6px' }}>{m.v}</div>
        </div>
      ))}
    </div>
    <div style={{ ...card, overflow: 'hidden' }}>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13.5px', minWidth: '680px' }}>
          <thead>
            <tr>
              <th style={{ ...thStyle, textAlign: 'left' }}>Vehicle</th>
              <th style={{ ...thStyle, textAlign: 'left' }}>Cargo</th>
              <th style={{ ...thStyle, textAlign: 'left' }}>Status</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>Fuel</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>Distance</th>
            </tr>
          </thead>
          <tbody>
            {D.lorries.map((l: any, i: number) => (
              <tr key={i} className="tc-rowhover" style={{ borderTop: '1px solid var(--hairline)' }}>
                <td style={{ padding: '13px 16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '11px' }}>
                    <span style={{ width: '34px', height: '34px', borderRadius: '9px', background: 'var(--amber-soft)', color: 'var(--amber)', display: 'grid', placeItems: 'center' }}>
                      <svg viewBox="0 0 24 24" width="17" height="17" fill="currentColor"><path d="M2 6a1 1 0 0 1 1-1h11a1 1 0 0 1 1 1v3h3.2a1 1 0 0 1 .8.4l2.6 3.4a1 1 0 0 1 .2.6V17a1 1 0 0 1-1 1h-1a2.5 2.5 0 0 1-5 0H9a2.5 2.5 0 0 1-5 0H3a1 1 0 0 1-1-1Z" /></svg>
                    </span>
                    <div>
                      <div style={{ fontWeight: 700 }}>{l.plate}</div>
                      <div style={{ fontSize: '11.5px', color: 'var(--t4)' }}>{l.model}</div>
                    </div>
                  </div>
                </td>
                <td style={{ padding: '13px 16px', color: 'var(--t2)' }}>{l.cargo}</td>
                <td style={{ padding: '13px 16px' }}>
                  <span style={busStatusChip(l.c)}><i style={{ width: '6px', height: '6px', borderRadius: '50%', background: 'currentColor' }} />{l.status}</span>
                </td>
                <td style={{ padding: '13px 16px', textAlign: 'right', fontFamily: mono, fontWeight: 600 }}>{l.fuel}</td>
                <td style={{ padding: '13px 16px', textAlign: 'right', fontFamily: mono, fontWeight: 600 }}>{l.dist}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  </div>
);

const CalendarPane: React.FC<any> = ({ D, card, mono }) => {
  const cal = D.calendar;
  const weekdays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const cells: { n: string; empty?: boolean; today?: boolean; color?: string }[] = [];
  for (let i = 0; i < cal.firstWeekdayOffset; i++) cells.push({ n: '', empty: true });
  for (let d = 1; d <= cal.daysInMonth; d++) cells.push({ n: String(d), today: d === cal.today, color: cal.eventColors[d] });
  return (
    <div className="tc-grid-cal">
      <div style={{ ...card, padding: '18px 20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
          <div style={{ fontSize: '16px', fontWeight: 700 }}>{cal.monthLabel}</div>
          <div style={{ display: 'flex', gap: '6px' }}>
            <button className="tc-softbtn" style={{ width: '32px', height: '32px', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--surface)', cursor: 'pointer', color: 'var(--t2)', display: 'grid', placeItems: 'center' }}><Ico d="M15 18l-6-6 6-6" size={16} sw={2} /></button>
            <button className="tc-softbtn" style={{ width: '32px', height: '32px', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--surface)', cursor: 'pointer', color: 'var(--t2)', display: 'grid', placeItems: 'center' }}><Ico d="M9 18l6-6-6-6" size={16} sw={2} /></button>
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: '6px' }}>
          {weekdays.map(w => <div key={w} style={{ textAlign: 'center', fontSize: '11px', fontWeight: 700, color: 'var(--t4)', textTransform: 'uppercase', letterSpacing: '0.05em', paddingBottom: '6px' }}>{w}</div>)}
          {cells.map((c, i) => c.empty ? <div key={i} style={{ aspectRatio: '1' }} /> : (
            <div key={i} style={{ aspectRatio: '1', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '4px', borderRadius: '9px', cursor: 'pointer', background: c.today ? 'var(--accent)' : 'transparent', border: `1px solid ${c.today ? 'var(--accent)' : 'transparent'}` }}>
              <span style={{ fontSize: '13px', fontWeight: c.today ? 700 : 500, fontFamily: mono, color: c.today ? '#fff' : 'var(--text)' }}>{c.n}</span>
              {c.color && <span style={{ width: '5px', height: '5px', borderRadius: '50%', background: c.today ? '#fff' : c.color }} />}
            </div>
          ))}
        </div>
      </div>
      <div style={{ ...card, padding: '18px 20px' }}>
        <H3 style={{ marginBottom: '4px' }}>Upcoming</H3>
        <div style={{ fontSize: '12.5px', color: 'var(--t3)', marginBottom: '14px' }}>Across all schools</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {cal.events.map((e: any, i: number) => (
            <div key={i} style={{ display: 'flex', gap: '12px' }}>
              <div style={{ width: '46px', height: '46px', borderRadius: '11px', flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: e.c, background: `color-mix(in oklab,${e.c} 13%, transparent)` }}>
                <span style={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase' }}>{e.mon}</span>
                <span style={{ fontSize: '17px', fontWeight: 800, fontFamily: mono, lineHeight: 1 }}>{e.day}</span>
              </div>
              <div style={{ flex: 1, minWidth: 0, borderLeft: '2px solid var(--hairline)', paddingLeft: '12px' }}>
                <div style={{ fontSize: '13px', fontWeight: 600 }}>{e.title}</div>
                <div style={{ fontSize: '11.5px', color: 'var(--t4)', marginTop: '2px' }}>{e.meta}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const ContentPane: React.FC<any> = ({ D, card, mono }) => (
  <div className="tc-grid-3">
    {D.contentItems.map((c: any, i: number) => (
      <div key={i} style={{ ...card, overflow: 'hidden' }}>
        <div style={{ height: '120px', borderBottom: '1px solid var(--hairline)', background: 'repeating-linear-gradient(135deg,var(--soft) 0 10px,var(--sunken) 10px 20px)', display: 'grid', placeItems: 'center', color: 'var(--t4)', fontFamily: mono, fontSize: '11px' }}>{c.ph}</div>
        <div style={{ padding: '14px 16px' }}>
          <div style={{ fontWeight: 700, fontSize: '14px' }}>{c.title}</div>
          <div style={{ fontSize: '12px', color: 'var(--t3)', marginTop: '3px' }}>{c.meta}</div>
        </div>
      </div>
    ))}
  </div>
);

const REPORTS = [
  { title: 'Revenue report', desc: 'Collected fees, MRR and churn by school and plan.', d: 'M3 3v18h18M7 14l4-4 3 3 5-6' },
  { title: 'Fleet utilisation', desc: 'Trips, distance and fuel efficiency across vehicles.', d: 'M4 16V6a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v10M4 16h16' },
  { title: 'Attendance', desc: 'Student boarding and ridership per route.', d: 'M9 11l3 3L22 4M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11' },
  { title: 'Subscriptions', desc: 'Active, trial and expired plans with renewal dates.', d: ICON.revenue },
  { title: 'User growth', desc: 'New signups by role across the platform.', d: ICON.users },
  { title: 'Audit log', desc: 'Every admin action with actor and timestamp.', d: 'M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9zM14 3v6h6' },
];
const ReportsPane: React.FC<any> = ({ card, onGen }) => (
  <div className="tc-grid-3">
    {REPORTS.map((r, i) => (
      <div key={i} onClick={() => onGen(r.title)} className="tc-reportcard" style={{ ...card, padding: '20px', cursor: 'pointer' }}>
        <span style={{ width: '40px', height: '40px', borderRadius: '11px', background: 'var(--accent-soft)', color: 'var(--accent)', display: 'grid', placeItems: 'center' }}><Ico d={r.d} size={20} sw={1.8} /></span>
        <div style={{ fontWeight: 700, fontSize: '15px', marginTop: '14px' }}>{r.title}</div>
        <div style={{ fontSize: '12.5px', color: 'var(--t3)', marginTop: '4px', lineHeight: 1.5 }}>{r.desc}</div>
        <div style={{ fontSize: '12px', color: 'var(--accent)', fontWeight: 600, marginTop: '12px' }}>Generate →</div>
      </div>
    ))}
  </div>
);

const AnalyticsPane: React.FC<any> = ({ D, card, cardPad, mono }) => (
  <div>
    <div className="tc-grid-kpi" style={{ marginBottom: '16px' }}>
      {D.anaKpis.map((k: any, i: number) => (
        <div key={i} style={{ ...card, padding: '17px 19px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '9px', fontSize: '12.5px', color: 'var(--t3)', fontWeight: 600 }}>
            <span style={{ width: '28px', height: '28px', borderRadius: '8px', background: 'var(--accent-soft)', display: 'grid', placeItems: 'center', color: 'var(--accent)' }}><Ico d={k.d} size={15} /></span>
            {k.label}
          </div>
          <div style={{ fontSize: '26px', fontWeight: 800, letterSpacing: '-0.02em', marginTop: '13px', lineHeight: 1, fontFamily: mono }}>{k.value}</div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '12px' }}>
            <span style={{ fontSize: '12.5px', fontWeight: 700, color: k.up ? 'var(--green)' : 'var(--red)' }}>{k.delta}</span>
            <span style={{ fontSize: '11.5px', color: 'var(--t4)' }}>{k.note}</span>
          </div>
        </div>
      ))}
    </div>

    <div className="tc-grid-half" style={{ marginBottom: '16px' }}>
      {/* fuel */}
      <div style={cardPad}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
          <H3>Fuel consumption by school</H3>
          <span style={{ fontSize: '11.5px', color: 'var(--t4)' }}>km / litre · higher is better</span>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
          {D.fuelPerSchool.map((s: any, i: number) => (
            <div key={i}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '13px', marginBottom: '6px' }}>
                <span style={{ fontWeight: 600 }}>{s.name}</span>
                <span style={{ fontFamily: mono, color: 'var(--t2)', fontWeight: 600 }}>{s.v}</span>
              </div>
              <div style={{ height: '8px', borderRadius: '999px', background: 'var(--sunken)', overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${s.pct}%`, borderRadius: '999px', background: s.good ? 'linear-gradient(90deg,var(--green),color-mix(in oklab,var(--green) 70%, #000))' : 'linear-gradient(90deg,var(--amber),color-mix(in oklab,var(--amber) 70%, #000))' }} />
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* driver ratings */}
      <div style={cardPad}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
          <H3>Driver rating by school</H3>
          <span style={{ fontSize: '11.5px', color: 'var(--t4)' }}>avg / 5</span>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {D.driverRatings.map((s: any, i: number) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '11px 0', borderBottom: '1px solid var(--hairline)' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '13px', fontWeight: 600 }}>{s.name}</div>
                <div style={{ fontSize: '11.5px', color: 'var(--t4)' }}>{s.drivers}</div>
              </div>
              <div style={{ display: 'flex', gap: '2px' }}>{starsFor(s.v).map((f, k) => <Star key={k} fill={f} size={14} />)}</div>
              <span style={{ fontFamily: mono, fontWeight: 700, fontSize: '13px', width: '30px', textAlign: 'right' }}>{s.v.toFixed(1)}</span>
            </div>
          ))}
        </div>
      </div>
    </div>

    <div className="tc-grid-half">
      {/* revenue lost */}
      <div style={cardPad}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
          <H3>Revenue lost by school</H3>
          <span style={{ fontSize: '11.5px', color: 'var(--red)', fontWeight: 700 }}>{D.revLostTotal}</span>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
          {D.revLost.map((s: any, i: number) => (
            <div key={i}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '13px', marginBottom: '6px' }}>
                <span style={{ fontWeight: 600 }}>{s.name}</span>
                <span style={{ fontFamily: mono, color: 'var(--red)', fontWeight: 700 }}>{s.v}</span>
              </div>
              <div style={{ height: '8px', borderRadius: '999px', background: 'var(--sunken)', overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${s.pct}%`, borderRadius: '999px', background: 'linear-gradient(90deg,var(--red),color-mix(in oklab,var(--red) 70%, #000))' }} />
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* distance bars */}
      <div style={cardPad}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
          <H3>Distance · this month vs last</H3>
        </div>
        <div style={{ fontSize: '12.5px', color: 'var(--t3)', marginBottom: '8px' }}>{D.distanceTotalLabel}</div>
        <svg viewBox="0 0 520 200" preserveAspectRatio="none" style={{ width: '100%', height: '190px' }}>
          {[50, 100, 150].map(y => <line key={y} x1="0" y1={y} x2="520" y2={y} stroke="var(--hairline)" strokeWidth={1} />)}
          <g>
            {D.distancePairs.map((p: any, i: number) => {
              const gx = i * 104;
              const h1 = p.last * 130, h2 = p.thisM * 130;
              return (
                <React.Fragment key={i}>
                  <rect x={gx + 24} y={186 - h1} width={26} height={h1} rx={5} fill="var(--accent-tint)" />
                  <rect x={gx + 56} y={186 - h2} width={26} height={h2} rx={5} fill="var(--accent)" />
                </React.Fragment>
              );
            })}
          </g>
        </svg>
        <div style={{ display: 'flex', gap: '16px', marginTop: '10px' }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '7px', fontSize: '12px', color: 'var(--t2)' }}><i style={{ width: '9px', height: '9px', borderRadius: '3px', background: 'var(--accent-tint)' }} />Last month</span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '7px', fontSize: '12px', color: 'var(--t2)' }}><i style={{ width: '9px', height: '9px', borderRadius: '3px', background: 'var(--accent)' }} />This month</span>
        </div>
      </div>
    </div>
  </div>
);

/* ════════════════════ ORG DRILLDOWN ════════════════════ */
const CONN_META: Record<string, { label: string; color: string }> = {
  connected: { label: 'Connected', color: 'var(--green)' },
  error: { label: 'Needs attention', color: 'var(--red)' },
  disconnected: { label: 'Not connected', color: 'var(--t3)' },
};
const avColorFor = (code: string) => AV_COLORS[Math.abs(code.split('').reduce((a, c) => a + c.charCodeAt(0), 0)) % AV_COLORS.length];

const OrgDetailPane: React.FC<any> = ({ org, card, mono, back, openApi, onEdit, onSaveHome, onArchive, onPurge, onResetRouteLearning, onRouteLearningStatus, onRouteLearningHeal, onResetFuelData }) => {
  const navigate = useNavigate();
  const stats = [
    { k: 'Students', v: org.students }, { k: 'Vehicles', v: org.buses },
    { k: 'MRR', v: org.mrr === '0' || !org.mrr ? '—' : `KES ${org.mrr}` },
    /* "Renews" is a promise. For a cancelling school that date is when the org
       CLOSES — the opposite — so the label has to follow the fact. And once it
       HAS closed it is neither: a school reading "Expired" beside "Closes 01
       Sep 2026" is being told in the future tense about something that already
       happened (Gabriel, 2026-09-02). Three states, three words. */
    { k: org.closed ? 'Closed' : org.canceling ? 'Closes' : 'Renews', v: org.end || '—' },
  ];

  // Removal is refused outright while a plan is live — see guardRemoval.
  const removal = guardRemoval(org.status, org.plan, { hasActiveSubscription: org.hasActiveSubscription });
  const [removeMode, setRemoveMode] = useState<'archive' | 'purge' | null>(null);
  const [removeTyped, setRemoveTyped] = useState('');
  const [removeBusy, setRemoveBusy] = useState(false);
  const [removeError, setRemoveError] = useState<string | null>(null);
  useEffect(() => { setRemoveTyped(''); setRemoveError(null); }, [removeMode]);
  // Route-learning reset: two clicks (arm, confirm), then the result inline.
  const [resetState, setResetState] = useState<'idle' | 'armed' | 'busy' | 'done' | 'error'>('idle');
  const [resetMsg, setResetMsg] = useState<string | null>(null);
  // Route-learning readout (bug 129) — read-only, what the learner sees.
  const [lsBusy, setLsBusy] = useState(false);
  const [lsErr, setLsErr] = useState<string | null>(null);
  const [lsNote, setLsNote] = useState<string | null>(null);
  // Fuel-data reset (same arm → confirm → done shape as the route reset).
  const [fuelResetState, setFuelResetState] = useState<'idle' | 'armed' | 'busy' | 'done' | 'error'>('idle');
  /* Off-trip tracking for THIS org. `null` = no override stored, so the app's
     own compiled default applies — a real third state, not a missing value. */
  const [wh, setWh] = useState<boolean | null>(null);
  const [whState, setWhState] = useState<'idle' | 'busy' | 'error'>('idle');
  const [whMsg, setWhMsg] = useState<string | null>(null);
  const [fuelResetMsg, setFuelResetMsg] = useState<string | null>(null);
  const [ls, setLs] = useState<RouteLearningStatus | null>(null);
  useEffect(() => { setResetState('idle'); setResetMsg(null); }, [org?.code]);
  // Read the org's current off-trip-tracking setting whenever the pane changes
  // orgs, so the three buttons show what is actually stored rather than a guess.
  useEffect(() => {
    let alive = true;
    setWh(null); setWhState('idle'); setWhMsg(null);
    const code = org?.code;
    if (!code) return;
    fetchWorkingHours(code).then((r) => {
      if (!alive) return;
      if (r.ok) setWh(r.enabled);
    }).catch(() => {});
    return () => { alive = false; };
  }, [org?.code]);

  const runRemoval = async () => {
    if (!removeMode) return;
    setRemoveBusy(true);
    setRemoveError(null);
    try {
      const purge = removeMode !== 'archive';
      if (purge) await onPurge?.(org);
      else await onArchive?.(org);
      setRemoveMode(null);
      // A purged school has no page to stay on — straight back to the list.
      if (purge) back();
    } catch (e: any) {
      setRemoveError(e?.message || 'That did not go through. Nothing was changed.');
    } finally {
      setRemoveBusy(false);
    }
  };
  const home: LatLng | null = org.homeLocations && typeof org.homeLocations.lat === 'number' ? org.homeLocations : null;
  const [homeModal, setHomeModal] = useState(false);
  const [homePin, setHomePin] = useState<LatLng | null>(home);
  const [homeSaving, setHomeSaving] = useState(false);
  const openHomeModal = () => { setHomePin(home); setHomeModal(true); };
  const saveHome = async () => {
    if (!homePin) return;
    setHomeSaving(true);
    try { await onSaveHome?.(org.code, homePin); setHomeModal(false); }
    finally { setHomeSaving(false); }
  };
  return (
    <div className="tc-fade">
      <button onClick={back} className="tc-contact" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', font: 'inherit', fontSize: '13px', fontWeight: 600, color: 'var(--t2)', background: 'none', border: 'none', cursor: 'pointer', padding: 0, marginBottom: '16px' }}>
        <Ico d="M19 12H5M12 19l-7-7 7-7" size={16} sw={2} />All schools
      </button>
      <div style={{ ...card, padding: '22px 24px', marginBottom: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
          <span style={{ width: '52px', height: '52px', borderRadius: '14px', display: 'grid', placeItems: 'center', fontSize: '19px', fontWeight: 700, color: '#fff', flexShrink: 0, background: avColorFor(org.code) }}>{initialsOf(org.name)}</span>
          <div style={{ flex: 1, minWidth: '180px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
              <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 800, letterSpacing: '-0.02em' }}>{org.name}</h2>
              <span style={orgStatusChip(org.status)}><i style={{ width: '6px', height: '6px', borderRadius: '50%', background: 'currentColor' }} />{org.status}</span>
              {org.beta && <span style={{ fontSize: '9.5px', fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--accent)', background: 'var(--accent-soft)', padding: '3px 7px', borderRadius: '5px' }}>Beta · unbilled</span>}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '5px', fontSize: '12.5px', color: 'var(--t3)' }}>
              <span style={{ fontFamily: mono }}>{org.code}</span><span>·</span><span>{org.plan} plan</span>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '9px', flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={() => navigate(`/admin/org/${encodeURIComponent(org.code)}/settings`)}
              className="tc-softbtn"
              style={{ font: 'inherit', fontSize: '13px', fontWeight: 600, background: 'var(--accent)', border: '1px solid transparent', borderRadius: '10px', padding: '9px 15px', cursor: 'pointer', color: '#fff' }}
            >
              Settings
            </button>
            {/* "Edit school" lived here and did nothing: its handler was
                optional and never supplied, so every click landed on a toast
                pointing at the Add-school wizard, which only creates. Settings
                is the editor (Gabriel, 2026-09-22: "what does it even do?? …
                can we do away with it"). */}
          </div>
        </div>
        <div className="tc-grid-4" style={{ marginTop: '20px' }}>
          {stats.map(s => (
            <div key={s.k} style={{ background: 'var(--soft)', border: '1px solid var(--hairline)', borderRadius: 'var(--r)', padding: '12px 14px' }}>
              <div style={{ fontSize: '11px', color: 'var(--t3)', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>{s.k}</div>
              <div style={{ fontSize: '17px', fontWeight: 800, fontFamily: mono, marginTop: '5px' }}>{s.v}</div>
            </div>
          ))}
        </div>
      </div>
      <div style={{ marginBottom: '16px' }}>
        <ZanaCapPanel orgCode={org.code} card={card} mono={mono} />
      </div>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', margin: '22px 4px 14px' }}>
        <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 700 }}>Integrations &amp; add-ons</h3>
        <span style={{ fontSize: '12.5px', color: 'var(--t3)' }}>External systems this school connects to</span>
      </div>
      <div className="tc-grid-2">
        {/* Home location — geofencing anchor. NOT a catalog add-on; styled to match the add-on cards. */}
        {(() => {
          const isSet = !!home;
          const pillColor = isSet ? 'var(--green)' : 'var(--amber)';
          const pillLabel = isSet ? 'Location set' : 'Needs attention';
          return (
            <div style={{ ...card, padding: '18px 20px', display: 'flex', flexDirection: 'column' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '13px' }}>
                <span style={{ width: '42px', height: '42px', borderRadius: '11px', flexShrink: 0, display: 'grid', placeItems: 'center', color: 'var(--accent)', background: 'color-mix(in oklab,var(--accent) 15%, transparent)' }}><Ico d="M12 21s-7-6.2-7-11a7 7 0 1 1 14 0c0 4.8-7 11-7 11zM12 10a1.5 1.5 0 1 0 0 .01" size={21} /></span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '9px', flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: 700, fontSize: '14.5px' }}>Home location</span>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '11px', fontWeight: 700, padding: '3px 9px', borderRadius: '999px', color: pillColor, background: `color-mix(in oklab,${pillColor} 14%, transparent)` }}><i style={{ width: '6px', height: '6px', borderRadius: '50%', background: 'currentColor' }} />{pillLabel}</span>
                  </div>
                  <div style={{ fontSize: '12.5px', color: 'var(--t3)', marginTop: '3px', lineHeight: 1.45 }}>Campus coordinate used to geofence arrivals &amp; departures</div>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '14px', padding: '9px 12px', background: 'var(--soft)', border: '1px solid var(--hairline)', borderRadius: '9px', fontSize: '12px' }}>
                <Ico d="M12 21s-7-6.2-7-11a7 7 0 1 1 14 0c0 4.8-7 11-7 11zM12 10a1.5 1.5 0 1 0 0 .01" size={14} sw={2} stroke="var(--t3)" />
                <span style={{ color: 'var(--t2)', fontWeight: 600, fontFamily: isSet ? mono : 'inherit' }}>{isSet ? `${home!.lat.toFixed(5)}, ${home!.lng.toFixed(5)}` : 'No location set'}</span>
              </div>
              <button onClick={openHomeModal} className={isSet ? 'tc-softbtn' : 'tc-accentbtn'} style={{ display: 'inline-flex', alignItems: 'center', gap: '7px', width: '100%', justifyContent: 'center', fontFamily: 'inherit', fontWeight: 600, fontSize: '12.5px', padding: '9px', borderRadius: '9px', cursor: 'pointer', marginTop: '14px', border: `1px solid ${isSet ? 'var(--border)' : 'var(--accent-strong)'}`, background: isSet ? 'var(--surface)' : 'var(--accent)', color: isSet ? 'var(--text)' : 'var(--accent-fg)' }}>
                {isSet ? 'Edit location' : 'Set home location'}
                <Ico d="M9 18l6-6-6-6" size={14} sw={2} />
              </button>
            </div>
          );
        })()}
        {ADDON_CATALOG.map(a => {
          const conn: AddonConn = org.conns?.[a.id] || { status: 'disconnected' };
          const meta = CONN_META[conn.status] || CONN_META.disconnected;
          const isConn = conn.status === 'connected', isErr = conn.status === 'error';
          return (
            <div key={a.id} style={{ ...card, padding: '18px 20px', display: 'flex', flexDirection: 'column' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '13px' }}>
                <span style={{ width: '42px', height: '42px', borderRadius: '11px', flexShrink: 0, display: 'grid', placeItems: 'center', color: a.color, background: `color-mix(in oklab,${a.color} 15%, transparent)` }}><Ico d={a.d} size={21} /></span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '9px', flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: 700, fontSize: '14.5px' }}>{a.name}</span>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '11px', fontWeight: 700, padding: '3px 9px', borderRadius: '999px', color: meta.color, background: `color-mix(in oklab,${meta.color} 14%, transparent)` }}><i style={{ width: '6px', height: '6px', borderRadius: '50%', background: 'currentColor' }} />{meta.label}</span>
                  </div>
                  <div style={{ fontSize: '12.5px', color: 'var(--t3)', marginTop: '3px', lineHeight: 1.45 }}>{a.tagline}</div>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '14px', padding: '9px 12px', background: 'var(--soft)', border: '1px solid var(--hairline)', borderRadius: '9px', fontSize: '12px' }}>
                <Ico d="M10 13a5 5 0 0 0 7 0l3-3a5 5 0 0 0-7-7l-1 1M14 11a5 5 0 0 0-7 0l-3 3a5 5 0 0 0 7 7l1-1" size={14} sw={2} stroke="var(--t3)" />
                <span style={{ color: 'var(--t2)', fontWeight: 600 }}>{conn.provider || 'No provider selected'}</span>
              </div>
              <button onClick={() => openApi(a.id, conn.provider || null)} className={isConn ? 'tc-softbtn' : 'tc-accentbtn'} style={{ display: 'inline-flex', alignItems: 'center', gap: '7px', width: '100%', justifyContent: 'center', fontFamily: 'inherit', fontWeight: 600, fontSize: '12.5px', padding: '9px', borderRadius: '9px', cursor: 'pointer', marginTop: '14px', border: `1px solid ${isConn ? 'var(--border)' : 'var(--accent-strong)'}`, background: isConn ? 'var(--surface)' : 'var(--accent)', color: isConn ? 'var(--text)' : 'var(--accent-fg)' }}>
                {isConn ? 'Manage connection' : (isErr ? 'Fix connection' : 'Connect')}
                <Ico d="M9 18l6-6-6-6" size={14} sw={2} />
              </button>
            </div>
          );
        })}
      </div>

      {/* ══ DANGER ZONE — archive (reversible) vs delete (not) ══ */}
      <div style={{ ...card, padding: '22px 24px', marginTop: '16px', borderColor: 'var(--red)' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', marginBottom: '14px' }}>
          <span style={{ width: '34px', height: '34px', borderRadius: '10px', background: 'rgba(220,38,38,0.10)', color: 'var(--red)', display: 'grid', placeItems: 'center', flexShrink: 0 }}>
            <Ico d="M12 9v4M12 17h.01M10.3 3.9 2.4 17.5A2 2 0 0 0 4.1 20.5h15.8a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" size={18} sw={2} />
          </span>
          <div>
            <div style={{ fontSize: '15px', fontWeight: 700 }}>Danger zone</div>
            <div style={{ fontSize: '12.5px', color: 'var(--t3)', marginTop: '2px' }}>Removing {org.name} from the platform.</div>
          </div>
        </div>

        {!removal.allowed ? (
          <div style={{ display: 'flex', gap: '9px', alignItems: 'flex-start', background: 'rgba(217,119,6,0.08)', border: '1px solid rgba(217,119,6,0.35)', borderRadius: '10px', padding: '12px 14px', fontSize: '12.5px', lineHeight: 1.55, color: 'var(--t2)' }}>
            <span style={{ color: '#d97706', flexShrink: 0, marginTop: '1px' }}><Ico d="M12 8v5M12 16h.01M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18z" size={16} sw={2} /></span>
            <span><b>Blocked while billing is live.</b> {removal.reason}</span>
          </div>
        ) : (
          <div style={{ display: 'grid', gap: '10px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '14px', flexWrap: 'wrap', border: '1px solid var(--border)', borderRadius: '10px', padding: '13px 15px' }}>
              <div style={{ maxWidth: '520px' }}>
                <div style={{ fontSize: '13.5px', fontWeight: 600 }}>Archive</div>
                <div style={{ fontSize: '12.5px', color: 'var(--t3)', marginTop: '2px', lineHeight: 1.5 }}>Takes a full snapshot, then hides the school from normal lists. Nothing is destroyed and it can be restored. <b>Use this one unless you are certain.</b></div>
              </div>
              <button onClick={() => setRemoveMode('archive')} className="tc-softbtn" style={{ font: 'inherit', fontSize: '13px', fontWeight: 600, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '10px', padding: '9px 16px', cursor: 'pointer', color: 'var(--text)', flexShrink: 0 }}>Archive school</button>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '14px', flexWrap: 'wrap', border: '1px solid rgba(220,38,38,0.35)', borderRadius: '10px', padding: '13px 15px' }}>
              <div style={{ maxWidth: '520px' }}>
                <div style={{ fontSize: '13.5px', fontWeight: 600, color: 'var(--red)' }}>Delete permanently</div>
                <div style={{ fontSize: '12.5px', color: 'var(--t3)', marginTop: '2px', lineHeight: 1.5 }}>
                  Erases the school and <b>every account attached to it</b> — parents, drivers, teachers, admins — plus classes, posts, events, conversations and notifications. Cannot be undone.
                  {!onPurge && <><br /><span style={{ color: '#d97706', fontWeight: 600 }}>Not available from the console yet — run it from the school dashboard. See the handoff doc.</span></>}
                </div>
              </div>
              <button
                onClick={() => onPurge && setRemoveMode('purge')}
                disabled={!onPurge}
                style={{ font: 'inherit', fontSize: '13px', fontWeight: 600, background: onPurge ? 'var(--red)' : 'var(--sunken)', color: onPurge ? '#fff' : 'var(--t4)', border: `1px solid ${onPurge ? 'var(--red)' : 'var(--border)'}`, borderRadius: '10px', padding: '9px 16px', cursor: onPurge ? 'pointer' : 'not-allowed', flexShrink: 0 }}
              >Delete permanently</button>
            </div>
          </div>
        )}

        {/* Route-learning reset. Not removal, but it lives with the other
            irreversible buttons. Gabriel, 2026-08-22: the Routes tab was full
            of clusters from desk tests and a day of drive tests with the org
            pin moved around — "delete all the routes that were there before so
            I can start working with this new data." Deletes the route profiles
            and the road aggregates, stamps the org, and reconcile / road sweep
            honour the stamp. Trips are untouched: Recent runs keeps them all. */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '14px', flexWrap: 'wrap', border: '1px solid var(--border)', borderRadius: '10px', padding: '13px 15px', marginTop: '10px' }}>
          <div style={{ maxWidth: '520px' }}>
            <div style={{ fontSize: '13.5px', fontWeight: 600 }}>Reset route learning</div>
            <div style={{ fontSize: '12.5px', color: 'var(--t3)', marginTop: '2px', lineHeight: 1.5 }}>
              Forgets every learned and half-learned route for {org.name} and starts again from the next trip. The trips themselves are kept — Recent runs on the bus page does not change.
              {resetMsg && <><br /><span style={{ color: resetState === 'error' ? 'var(--red)' : resetState === 'armed' ? '#d97706' : 'var(--green, #3da233)', fontWeight: 600 }}>{resetMsg}</span></>}
            </div>
          </div>
          <button
            type="button"
            onClick={async () => {
              if (!onResetRouteLearning) return;
              if (resetState !== 'armed') {
                setResetState('armed');
                setResetMsg('Click again to confirm — this cannot be undone.');
                return;
              }
              setResetState('busy');
              setResetMsg(null);
              try {
                const out = await onResetRouteLearning(org);
                const n = out && typeof out === 'object' ? (out as { deleted?: number }).deleted : undefined;
                setResetState('done');
                setResetMsg(typeof n === 'number'
                  ? `Done — ${n} route profile${n === 1 ? '' : 's'} cleared. Learning restarts with the next trip.`
                  : 'Done — learning restarts with the next trip.');
              } catch (e: any) {
                setResetState('error');
                setResetMsg(e?.message || 'Could not reset route learning.');
              }
            }}
            disabled={!onResetRouteLearning || resetState === 'busy'}
            className="tc-softbtn"
            style={{ font: 'inherit', fontSize: '13px', fontWeight: 600, background: resetState === 'armed' ? 'var(--red)' : 'var(--surface)', color: resetState === 'armed' ? '#fff' : 'inherit', border: `1px solid ${resetState === 'armed' ? 'var(--red)' : 'var(--border)'}`, borderRadius: '10px', padding: '9px 14px', cursor: onResetRouteLearning ? 'pointer' : 'not-allowed' }}
          >{resetState === 'busy' ? 'Resetting…' : resetState === 'armed' ? 'Confirm reset' : 'Reset route learning'}</button>
        </div>

        {/* Route-learning readout (bug 129). Gabriel, 2026-08-22: three evening
            runs on each of two buses, Routes tab empty — "how does it work??".
            Read-only: the trips since the last reset with the exact inputs the
            learner reads, and the clusters it would form (same bus, same
            morning/evening run, start AND end within 150 m; learned at 3). */}
        <div style={{ border: '1px solid var(--border)', borderRadius: '12px', padding: '14px 16px', marginTop: '12px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '14px', flexWrap: 'wrap' }}>
            <div style={{ maxWidth: '520px' }}>
              <div style={{ fontSize: '13.5px', fontWeight: 600 }}>Check route learning</div>
              <div style={{ fontSize: '12.5px', color: 'var(--t3)', marginTop: '2px', lineHeight: 1.5 }}>
                Why a route has or hasn’t learned for {org.name}: every completed trip since the last reset with what the learner reads, and the clusters it forms — same bus, same morning/evening run, start and end within {ls?.anchorToleranceM ?? 150} m, learned at {ls?.learnedAfterTrips ?? 3}.
              </div>
            </div>
            <button
              type="button"
              className="tc-softbtn"
              disabled={!onRouteLearningStatus || lsBusy}
              onClick={async () => {
                if (!onRouteLearningStatus) return;
                setLsBusy(true); setLsErr(null);
                try { setLs(await onRouteLearningStatus(org)); } catch (e: any) { setLsErr(e?.message || 'Could not read route learning.'); } finally { setLsBusy(false); }
              }}
              style={{ font: 'inherit', fontSize: '13px', fontWeight: 600, background: 'var(--surface)', color: 'inherit', border: '1px solid var(--border)', borderRadius: '10px', padding: '9px 14px', cursor: onRouteLearningStatus ? 'pointer' : 'not-allowed' }}
            >{lsBusy ? 'Checking…' : 'Check learning'}</button>
          </div>
          {lsErr && <div style={{ color: 'var(--red)', fontSize: '12.5px', marginTop: '8px' }}>{lsErr}</div>}
          {ls && ls.ok && (() => {
            const profiles = ls.profiles ?? [];
            const trips = ls.trips ?? [];
            const clusters = ls.clusters ?? [];
            const need = ls.learnedAfterTrips ?? 3;
            const f4 = (n: number | null | undefined) => (n == null ? '—' : n.toFixed(4));
            return (
              <div style={{ marginTop: '10px', fontSize: '12.5px', lineHeight: 1.6 }}>
                <div>
                  Reset: <b>{ls.resetAt ? new Date(ls.resetAt).toLocaleString() : 'never'}</b> · profiles: <b>{profiles.length}</b> ({profiles.filter((p) => p.isLearned).length} learned) · trips since reset: <b>{trips.length}</b>
                </div>
                {clusters.length === 0 && trips.length === 0 && (
                  <div style={{ color: 'var(--t3)' }}>No completed trip since the reset.</div>
                )}
                {clusters.length === 0 && trips.length > 0 && (() => {
                  /* Say WHY in one line — the first press showed "no learnable
                     trip" with the reasons folded away (Gabriel, 2026-08-22). */
                  const counts = new Map<string, number>();
                  trips.forEach((t) => { const k = t.skip || 'ok'; counts.set(k, (counts.get(k) ?? 0) + 1); });
                  const parts = [...counts.entries()].map(([k, n]) => `${k} ×${n}`).join(' · ');
                  return (
                    <div style={{ color: '#b45309' }}>
                      Every trip since the reset was skipped by the learner — <b>{parts}</b>.
                      {counts.has('no_endpoints') && ' “no_endpoints” = the trip has no usable start/end position (no readable trail point and no pickup/drop pin), so neither the phone nor the 09:00 rebuild can place it.'}
                      {counts.has('no_gps_trail') && ' “no_gps_trail” = the phone had no GPS breadcrumbs at finish.'}
                      {counts.has('not_a_daily_run') && ' “not_a_daily_run” = not a morning/evening run (refuel / school trip) — never learned, by design.'}
                      {counts.has('under_min_km') && ' “under_min_km” = under 0.5 km.'}
                    </div>
                  );
                })()}
                {clusters.map((c, i) => (
                  <div key={i}>
                    <b>{c.busId}</b> · {c.bucket.toLowerCase()} · <b>{c.trips}/{need}</b> trips · {f4(c.sLat)},{f4(c.sLng)} → {f4(c.eLat)},{f4(c.eLng)} · {c.wouldBeLearned ? '✓ enough to learn' : 'still learning'}
                  </div>
                ))}
                {clusters.length > 0 && clusters.some((c) => c.wouldBeLearned) && profiles.filter((p) => p.isLearned).length === 0 && (
                  <div style={{ color: '#d97706' }}>Enough matching trips, but no learned profile exists — the phone’s write is failing (rules / queue); the 09:00 EAT reconcile will build it from the trip log.</div>
                )}
                {(() => {
                  const healable = trips.filter((t) => t.skip === 'no_endpoints' && (t.points ?? 0) >= 2);
                  // Morning runs stamped under the old first-pickup rule get
                  // re-anchored to where Start was pressed (154/156).
                  const morning = trips.filter((t) => t.skip !== 'no_endpoints'
                    && (t.routeType || '').toLowerCase() === 'morning' && (t.points ?? 0) >= 2);
                  const workload = [...healable, ...morning];
                  if (!workload.length || !onRouteLearningHeal) return null;
                  return (
                    <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                      <button
                        type="button"
                        className="tc-softbtn"
                        disabled={lsBusy}
                        onClick={async () => {
                          setLsBusy(true); setLsErr(null); setLsNote(null);
                          try {
                            const r = await onRouteLearningHeal(org, workload);
                            setLsNote(
                              `Healed / re-anchored ${r.healed} of ${workload.length} trip${workload.length === 1 ? '' : 's'}`
                              + (r.rebuilt != null ? ` · rebuilt ${r.rebuilt} profile${r.rebuilt === 1 ? '' : 's'}` : ' · routes rebuilt')
                              /* The snap truth — so a broken key is a sentence
                                 here, never a silently unchanged map. */
                              + (r.snapOn === false ? ' · ⚠️ road snap OFF — GOOGLE_MAPS_API_KEY is not set on the server' : '')
                              + (r.snapOn === true ? ` · road snap: ${r.snapped ?? 0} snapped, ${r.snapFailed ?? 0} failed${r.snapError ? ` — ${r.snapError.slice(0, 90)}` : ''}` : '')
                              + (r.roadsNamed || r.nameErrors || r.nameError
                                ? ` · road names: ${r.roadsNamed ?? 0} named${r.nameErrors ? `, ${r.nameErrors} failed` : ''}${r.nameError ? ` — ${String(r.nameError).slice(0, 80)}` : ''}`
                                : '')
                              + (r.error ? ` · ${r.error}` : ''),
                            );
                            if (onRouteLearningStatus) setLs(await onRouteLearningStatus(org));
                          } catch (e: any) {
                            setLsErr(e?.message || 'Heal failed.');
                          } finally {
                            setLsBusy(false);
                          }
                        }}
                        style={{ font: 'inherit', fontSize: '13px', fontWeight: 600, background: 'var(--surface)', color: 'inherit', border: '1px solid var(--border)', borderRadius: '10px', padding: '9px 14px', cursor: lsBusy ? 'wait' : 'pointer' }}
                      >{lsBusy ? 'Working…' : `Heal / re-anchor ${workload.length} trip${workload.length === 1 ? '' : 's'} & rebuild routes`}</button>
                      <span style={{ fontSize: '12px', color: 'var(--t3)', maxWidth: '560px' }}>
                        Stamps the start/end the phone missed, moves morning starts to where Start was pressed (the new rule), then rebuilds this org’s routes now (not at 06:00).
                      </span>
                    </div>
                  );
                })()}
                {lsNote && <div style={{ color: 'var(--green, #3da233)', fontSize: '12.5px', marginTop: '6px' }}>{lsNote}</div>}
                {profiles.length > 0 && (
                  <div style={{ marginTop: '6px' }}>
                    Profiles: {profiles.map((p) => `${p.busId} ${p.routeBucket.toLowerCase()} ${p.tripCount}/${need}${p.isLearned ? ' ✓' : ''}`).join(' · ')}
                  </div>
                )}
                <details style={{ marginTop: '6px' }} open={clusters.length === 0 && trips.length > 0}>
                  <summary style={{ cursor: 'pointer' }}>Trips ({trips.length}) — when · bus · run · km · min · start → end · trail points · idle · road snap · ri stamp · skip</summary>
                  <div style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: '11.5px', whiteSpace: 'pre-wrap', marginTop: '4px' }}>
                    {trips.map((t) => [
                      t.completedAt.slice(0, 16).replace('T', ' '),
                      t.busId,
                      t.routeType || '—',
                      t.distanceKm != null ? `${t.distanceKm.toFixed(1)} km` : '— km',
                      t.durationMinutes != null ? `${Math.round(t.durationMinutes)} min` : '— min',
                      t.sLat != null ? `${f4(t.sLat)},${f4(t.sLng)} → ${f4(t.eLat)},${f4(t.eLng)}` : 'no endpoints',
                      `${t.points ?? 0} pts${t.distanceKmSource ? ` (${t.distanceKmSource})` : ''}`,
                      `idle ${t.idleSeconds != null ? `${Math.round(t.idleSeconds)} s` : '—'}`,
                      t.snappedKm != null ? `snap✓ ${t.snappedKm.toFixed(1)} km` : t.snapError ? `snap✗ ${t.snapError.slice(0, 40)}` : 'snap —',
                      t.hasRi ? 'ri✓' : 'ri✗',
                      t.skip ? `SKIP:${t.skip}` : '',
                    ].join('  ')).join('\n')}
                  </div>
                </details>
              </div>
            );
          })()}
        </div>

        {/* Fuel-data reset. Gabriel, 2026-08-23: "I need to be checking the
            fuel analytics actually work — is there a way we can wipe the data
            so I start again?" Deletes refuel events, station stops, the Fuel
            Engine's per-bus state / baselines / engine alerts, fuel-kind
            fleet alerts and the fuel fields cached on each bus. Keeps tank
            size, fuel type, benchmark, odometer unit, station prices / EPRA
            ceilings, and every trip. */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '14px', flexWrap: 'wrap', border: '1px solid var(--border)', borderRadius: '12px', padding: '14px 16px', marginTop: '12px' }}>
          <div style={{ maxWidth: '520px' }}>
            <div style={{ fontSize: '13.5px', fontWeight: 600 }}>Reset fuel data</div>
            <div style={{ fontSize: '12.5px', color: 'var(--t3)', marginTop: '2px', lineHeight: 1.5 }}>
              Wipes every refuel event, station stop, fuel-engine state and fuel alert for {org.name}, so the fuel analytics start from zero. Keeps each bus’s tank size, fuel type, benchmark and odometer unit, the station price list and EPRA ceilings, and all trips.
              {fuelResetMsg && <><br /><span style={{ color: fuelResetState === 'error' ? 'var(--red)' : fuelResetState === 'armed' ? '#d97706' : 'var(--green, #3da233)', fontWeight: 600 }}>{fuelResetMsg}</span></>}
            </div>
          </div>
          <button
            type="button"
            onClick={async () => {
              if (!onResetFuelData) return;
              if (fuelResetState !== 'armed') {
                setFuelResetState('armed');
                setFuelResetMsg('Click again to confirm — this cannot be undone.');
                return;
              }
              setFuelResetState('busy');
              setFuelResetMsg(null);
              try {
                const out = await onResetFuelData(org);
                const c = out && typeof out === 'object' ? (out as { counts?: Record<string, number> }).counts : undefined;
                setFuelResetState('done');
                setFuelResetMsg(c
                  ? `Done — ${c.refuelEvents ?? 0} refuel event(s), ${c.stationStops ?? 0} station stop(s), ${c.fuelStates ?? 0} engine state(s), ${c.fleetAlerts ?? 0} fuel alert(s) cleared across ${c.buses ?? 0} bus(es). Fuel analytics start from the next fill.`
                  : 'Done — fuel data cleared. Fuel analytics start from the next fill.');
              } catch (e: any) {
                setFuelResetState('error');
                setFuelResetMsg(e?.message || 'Could not reset fuel data.');
              }
            }}
            disabled={!onResetFuelData || fuelResetState === 'busy'}
            className="tc-softbtn"
            style={{ font: 'inherit', fontSize: '13px', fontWeight: 600, background: fuelResetState === 'armed' ? 'var(--red)' : 'var(--surface)', color: fuelResetState === 'armed' ? '#fff' : 'inherit', border: `1px solid ${fuelResetState === 'armed' ? 'var(--red)' : 'var(--border)'}`, borderRadius: '10px', padding: '9px 14px', cursor: onResetFuelData ? 'pointer' : 'not-allowed' }}
          >{fuelResetState === 'busy' ? 'Resetting…' : fuelResetState === 'armed' ? 'Confirm reset' : 'Reset fuel data'}</button>
        </div>

        {/* 💸 Working hours — the one policy that decides how often an
            UNCAPPED paid platform gets written to. Off-trip recording roughly
            DOUBLES GPS writes (+$2–4/mo at 2 buses, +$35–40 at 100, +$175–200
            at 500) and Blaze has no hard spend cap. It used to be a constant
            compiled into the app, so turning it OFF meant shipping a release
            to every driver phone. The org document carries it now: a flip here
            reaches drivers on their next trip. */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '14px', flexWrap: 'wrap', border: '1px solid var(--border)', borderRadius: '12px', padding: '14px 16px', marginTop: '12px' }}>
          <div style={{ maxWidth: '520px' }}>
            <div style={{ fontSize: '13.5px', fontWeight: 600 }}>Off-trip tracking (working hours)</div>
            <div style={{ fontSize: '12.5px', color: 'var(--t3)', marginTop: '2px', lineHeight: 1.5 }}>
              Records {org.name}&rsquo;s buses between 03:30 and 21:00 even when no trip is running, but only while actually moving. 💸 Roughly <b>doubles GPS writes</b>, and Firebase has no hard spend cap — this is the switch to reach for if a bill starts running. Drivers pick it up on their next trip; no app update needed.
              {whMsg && <><br /><span style={{ color: whState === 'error' ? 'var(--red)' : 'var(--green, #3da233)', fontWeight: 600 }}>{whMsg}</span></>}
            </div>
          </div>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {([['On', true], ['Off', false], ['App default', null]] as Array<[string, boolean | null]>).map(([label, value]) => (
              <button
                key={label}
                type="button"
                disabled={whState === 'busy'}
                onClick={async () => {
                  setWhState('busy');
                  setWhMsg(null);
                  const out = await setWorkingHours(org.code, value);
                  if (!out.ok) { setWhState('error'); setWhMsg(out.error || 'Could not save.'); return; }
                  setWh(out.enabled);
                  setWhState('idle');
                  setWhMsg(value === null
                    ? 'Cleared — the app\u2019s own default applies again.'
                    : value
                      ? 'On — off-trip recording resumes on each driver\u2019s next trip.'
                      : 'Off — buses record during trips only, from each driver\u2019s next trip.');
                }}
                className="tc-softbtn"
                style={{
                  font: 'inherit', fontSize: '13px', fontWeight: 600, borderRadius: '10px', padding: '9px 14px',
                  cursor: whState === 'busy' ? 'wait' : 'pointer',
                  background: wh === value ? 'var(--accent, #6f4ff0)' : 'var(--surface)',
                  color: wh === value ? '#fff' : 'inherit',
                  border: `1px solid ${wh === value ? 'var(--accent, #6f4ff0)' : 'var(--border)'}`,
                }}
              >{label}</button>
            ))}
          </div>
        </div>
      </div>

      {/* ══ REMOVAL CONFIRMATION — typing the code is the brake ══ */}
      {removeMode && (
        <div onClick={() => !removeBusy && setRemoveMode(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(15,15,25,0.42)', backdropFilter: 'blur(3px)', zIndex: 90, display: 'grid', placeItems: 'center', padding: '24px', animation: 'tc-scrimin 0.18s ease' }}>
          <div onClick={e => e.stopPropagation()} style={{ width: '100%', maxWidth: '520px', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--r-xl)', boxShadow: 'var(--sh-lg)', animation: 'tc-sheetin 0.24s cubic-bezier(.2,.7,.2,1) both' }}>
            <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--hairline)', display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
              <span style={{ width: '38px', height: '38px', borderRadius: '11px', background: removeMode === 'purge' ? 'rgba(220,38,38,0.10)' : 'var(--accent-soft)', color: removeMode === 'purge' ? 'var(--red)' : 'var(--accent)', display: 'grid', placeItems: 'center', flexShrink: 0 }}>
                <Ico d={removeMode === 'purge' ? 'M3 6h18M8 6V4h8v2M6 6l1 14h10l1-14M10 11v6M14 11v6' : 'M21 8v13H3V8M1 3h22v5H1zM10 12h4'} size={19} sw={2} />
              </span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '16px', fontWeight: 700 }}>{removeMode === 'purge' ? 'Delete this school permanently?' : 'Archive this school?'}</div>
                <div style={{ fontSize: '12.5px', color: 'var(--t3)', marginTop: '2px' }}>{org.name} · {org.code}</div>
              </div>
            </div>

            <div style={{ padding: '20px 24px' }}>
              {removeMode === 'purge' ? (
                <>
                  <div style={{ fontSize: '13px', lineHeight: 1.65, color: 'var(--t2)', marginBottom: '14px' }}>
                    This erases, with no way back:
                    <ul style={{ margin: '8px 0 0', paddingLeft: '18px' }}>
                      <li><b>Every account</b> in this school — parents, drivers, teachers and admins lose their logins</li>
                      <li>{org.students} students and {org.buses} vehicles</li>
                      <li>Classes, posts, events, conversations and notifications</li>
                      <li>Trips, routes, children, fuel and Zana records — every record that names this school</li>
                      <li>For a Trasse Families household, its free-trial record too, so the family can sign up again</li>
                    </ul>
                  </div>
                  <div style={{ fontSize: '12.5px', color: 'var(--t3)', marginBottom: '14px' }}>Only payment records are kept — no copy is saved. If you are not certain, archive instead: it keeps a full copy and can be reversed.</div>
                </>
              ) : (
                <div style={{ fontSize: '13px', lineHeight: 1.65, color: 'var(--t2)', marginBottom: '14px' }}>
                  Everything is snapshotted to <code>organization_archives</code> first, then the school is hidden from normal lists. Nobody loses an account, and this can be undone.
                </div>
              )}

              <label style={{ display: 'block', fontSize: '12.5px', fontWeight: 600, color: 'var(--t2)', marginBottom: '6px' }}>
                Type <code style={{ fontFamily: mono, background: 'var(--sunken)', padding: '1px 6px', borderRadius: '5px' }}>{org.code}</code> to confirm
              </label>
              <input
                autoFocus
                value={removeTyped}
                onChange={e => setRemoveTyped(e.target.value)}
                placeholder={org.code}
                className="tc-input"
                style={{ width: '100%', font: 'inherit', fontSize: '13px', fontFamily: mono, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--r)', padding: '10px 12px', color: 'var(--text)', outline: 'none' }}
              />
              {removeError && <div style={{ fontSize: '12.5px', color: 'var(--red)', marginTop: '8px' }}>{removeError}</div>}
            </div>

            <div style={{ padding: '16px 24px', borderTop: '1px solid var(--hairline)', display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button onClick={() => setRemoveMode(null)} disabled={removeBusy} className="tc-softbtn" style={{ font: 'inherit', fontSize: '13px', fontWeight: 600, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '10px', padding: '9px 16px', cursor: removeBusy ? 'not-allowed' : 'pointer', color: 'var(--text)' }}>Cancel</button>
              <button
                onClick={runRemoval}
                disabled={removeBusy || removeTyped.trim().toUpperCase() !== String(org.code).toUpperCase()}
                style={{ font: 'inherit', fontSize: '13px', fontWeight: 600, borderRadius: '10px', padding: '9px 16px',
                  background: removeMode === 'purge' ? 'var(--red)' : 'var(--accent)',
                  color: removeMode === 'purge' ? '#fff' : 'var(--accent-fg)',
                  border: `1px solid ${removeMode === 'purge' ? 'var(--red)' : 'var(--accent-strong)'}`,
                  cursor: removeBusy || removeTyped.trim().toUpperCase() !== String(org.code).toUpperCase() ? 'not-allowed' : 'pointer',
                  opacity: removeBusy || removeTyped.trim().toUpperCase() !== String(org.code).toUpperCase() ? 0.5 : 1 }}
              >{removeBusy ? 'Working…' : removeMode === 'purge' ? 'Delete permanently' : 'Archive school'}</button>
            </div>
          </div>
        </div>
      )}

      {/* ══ HOME LOCATION MODAL (backfill / edit) ══ */}
      {homeModal && (
        <div onClick={() => setHomeModal(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(15,15,25,0.42)', backdropFilter: 'blur(3px)', zIndex: 90, display: 'grid', placeItems: 'center', padding: '24px', animation: 'tc-scrimin 0.18s ease' }}>
          <div onClick={e => e.stopPropagation()} style={{ width: '100%', maxWidth: '560px', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--r-xl)', boxShadow: 'var(--sh-lg)', display: 'flex', flexDirection: 'column', maxHeight: '90vh', animation: 'tc-sheetin 0.24s cubic-bezier(.2,.7,.2,1) both' }}>
            <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--hairline)', display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
              <span style={{ width: '38px', height: '38px', borderRadius: '11px', background: 'var(--accent-soft)', color: 'var(--accent)', display: 'grid', placeItems: 'center', flexShrink: 0 }}><Ico d="M12 21s-7-6.2-7-11a7 7 0 1 1 14 0c0 4.8-7 11-7 11zM12 10a1.5 1.5 0 1 0 0 .01" size={19} sw={2} /></span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '16px', fontWeight: 700 }}>Home location</div>
                <div style={{ fontSize: '12.5px', color: 'var(--t3)', marginTop: '2px' }}>Drop a pin on {org.name}'s main gate — used for geofencing.</div>
              </div>
              <button onClick={() => setHomeModal(false)} className="tc-closebtn" style={{ background: 'none', border: 'none', color: 'var(--t3)', cursor: 'pointer', padding: '4px' }}><Ico d="M6 6l12 12M18 6L6 18" size={19} sw={2} /></button>
            </div>
            <div style={{ padding: '22px 24px', overflowY: 'auto' }}>
              <LocationPicker value={homePin} onChange={setHomePin} heightPx={300} />
            </div>
            <div style={{ padding: '16px 24px', borderTop: '1px solid var(--hairline)', display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button onClick={() => setHomeModal(false)} className="tc-softbtn" style={{ font: 'inherit', fontSize: '13px', fontWeight: 600, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '10px', padding: '9px 16px', cursor: 'pointer', color: 'var(--text)' }}>Cancel</button>
              <button onClick={saveHome} disabled={!homePin || homeSaving} className="tc-accentbtn" style={{ font: 'inherit', fontSize: '13px', fontWeight: 600, background: 'var(--accent)', color: 'var(--accent-fg)', border: '1px solid var(--accent-strong)', borderRadius: '10px', padding: '9px 16px', cursor: !homePin || homeSaving ? 'not-allowed' : 'pointer', boxShadow: 'var(--sh-xs)', opacity: !homePin || homeSaving ? 0.5 : 1 }}>{homeSaving ? 'Saving…' : 'Save location'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const TERM_COLOR: Record<string, string> = { cmd: 'var(--accent)', muted: 'var(--t3)', ok: 'var(--green)', err: 'var(--red)', done: 'var(--green)', fail: 'var(--red)' };
const ApiConnectPane: React.FC<any> = ({ org, def, conn, mono, selectedProvider, setProvider, enabled, toggleEnabled, testLog, testing, testResult, onRunTest, onSave, busy, backToOrgs, backToOrg }) => {
  const apiInput: CSS = { width: '100%', font: 'inherit', fontSize: '13px', fontFamily: mono, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--r)', padding: '10px 12px', color: 'var(--text)', outline: 'none' };
  const endpoint = conn?.endpoint || `https://api.trasse.io/${def.id}`;
  return (
    <div className="tc-fade">
      <div style={{ display: 'flex', alignItems: 'center', gap: '7px', fontSize: '13px', marginBottom: '16px', color: 'var(--t3)', flexWrap: 'wrap' }}>
        <span onClick={backToOrgs} className="tc-contact" style={{ cursor: 'pointer', fontWeight: 600 }}>Schools</span>
        <span>/</span>
        <span onClick={backToOrg} className="tc-contact" style={{ cursor: 'pointer', fontWeight: 600 }}>{org.name}</span>
        <span>/</span>
        <span style={{ color: 'var(--text)', fontWeight: 700 }}>{def.name}</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '16px', flexWrap: 'wrap', marginBottom: '20px' }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 800, letterSpacing: '-0.02em' }}>Connect {def.name}</h2>
          <div style={{ fontSize: '13px', color: 'var(--t3)', marginTop: '4px' }}>{def.tagline} · for <b style={{ color: 'var(--t2)' }}>{org.name}</b></div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '11px', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--r)', padding: '9px 14px' }}>
          <button onClick={toggleEnabled} style={{ width: '44px', height: '26px', borderRadius: '999px', border: 'none', cursor: 'pointer', position: 'relative', background: enabled ? 'var(--green)' : 'var(--border)', transition: 'background 0.2s', padding: 0, flexShrink: 0 }}>
            <span style={{ position: 'absolute', top: '3px', left: enabled ? '21px' : '3px', width: '20px', height: '20px', borderRadius: '50%', background: '#fff', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.3)' }} />
          </button>
          <span style={{ fontSize: '12.5px', fontWeight: 700 }}>{enabled ? 'Integration enabled' : 'Integration disabled'}</span>
        </div>
      </div>
      <div className="tc-grid-half">
        {/* provider */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--r-lg)', boxShadow: 'var(--sh-xs)', padding: '20px' }}>
            <div style={{ fontSize: '13.5px', fontWeight: 700, marginBottom: '4px' }}>Service provider</div>
            <div style={{ fontSize: '12px', color: 'var(--t3)', marginBottom: '14px' }}>Pick the external system this school actually uses.</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '9px' }}>
              {def.providers.map((p: string) => {
                const on = p === selectedProvider;
                return (
                  <button key={p} onClick={() => setProvider(p)} style={{ display: 'flex', alignItems: 'center', gap: '11px', width: '100%', textAlign: 'left', padding: '12px 14px', borderRadius: 'var(--r)', cursor: 'pointer', fontFamily: 'inherit', fontSize: '13px', fontWeight: 600, background: on ? 'var(--accent-soft)' : 'var(--surface)', border: `1px solid ${on ? 'var(--accent)' : 'var(--border)'}`, color: 'var(--text)' }}>
                    <span style={{ width: '16px', height: '16px', borderRadius: '50%', flexShrink: 0, display: 'grid', placeItems: 'center', border: `1px solid ${on ? 'var(--accent)' : 'var(--border)'}`, background: on ? 'var(--accent)' : 'transparent' }}>
                      {on && <Ico d="M20 6L9 17l-5-5" size={10} sw={3.5} stroke="#fff" />}
                    </span>
                    {p}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
        {/* credentials */}
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--r-lg)', boxShadow: 'var(--sh-xs)', padding: '20px' }}>
          <div style={{ fontSize: '13.5px', fontWeight: 700, marginBottom: '14px' }}>API credentials</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '13px' }}>
            <div>
              <label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--t2)', display: 'block', marginBottom: '6px' }}>Base URL / endpoint</label>
              <input id="tc-api-endpoint" className="tc-input" defaultValue={endpoint} style={apiInput} />
            </div>
            <div>
              <label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--t2)', display: 'block', marginBottom: '6px' }}>API key / username</label>
              <input id="tc-api-key" className="tc-input" placeholder="sk_live_••••••••" style={apiInput} />
            </div>
            <div>
              <label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--t2)', display: 'block', marginBottom: '6px' }}>Secret / password <span style={{ color: 'var(--t4)', fontWeight: 500 }}>(if required)</span></label>
              <input id="tc-api-secret" className="tc-input" type="password" placeholder="cs_••••••••••••" style={apiInput} />
            </div>
          </div>
          <button onClick={onSave} disabled={busy} className="tc-accentbtn" style={{ marginTop: '16px', width: '100%', font: 'inherit', fontSize: '13px', fontWeight: 600, background: 'var(--accent)', color: 'var(--accent-fg)', border: '1px solid var(--accent-strong)', borderRadius: '10px', padding: '10px', cursor: busy ? 'wait' : 'pointer', opacity: busy ? 0.7 : 1 }}>{busy ? 'Saving…' : 'Save connection'}</button>
        </div>
      </div>
      {/* test console */}
      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--r-lg)', boxShadow: 'var(--sh-xs)', padding: '20px', display: 'flex', flexDirection: 'column', marginTop: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px' }}>
          <div style={{ fontSize: '13.5px', fontWeight: 700 }}>Test connection</div>
          {testResult === 'ok' && <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '11px', fontWeight: 700, color: 'var(--green)', background: 'var(--green-soft)', padding: '3px 10px', borderRadius: '999px' }}>● Healthy</span>}
          {testResult === 'fail' && <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '11px', fontWeight: 700, color: 'var(--red)', background: 'var(--red-soft)', padding: '3px 10px', borderRadius: '999px' }}>● Failed</span>}
        </div>
        <div style={{ fontSize: '12px', color: 'var(--t3)', marginBottom: '14px' }}>Runs a live handshake against the provider and validates the schema.</div>
        <div style={{ flex: 1, background: '#0c0c14', border: '1px solid #26263a', borderRadius: 'var(--r)', padding: '15px 16px', fontFamily: mono, fontSize: '12px', lineHeight: 1.65, minHeight: '230px', overflow: 'auto' }}>
          {testLog.length > 0 ? (
            <div>
              {testLog.map((l: { t: string; m: string }, i: number) => (
                <div key={i} style={{ color: TERM_COLOR[l.t] || 'var(--t2)', fontWeight: (l.t === 'done' || l.t === 'fail' || l.t === 'cmd') ? 700 : 400, padding: '2px 0', animation: 'tc-fadeup 0.2s ease both' }}>{l.m}</div>
              ))}
              {testing && <div style={{ color: 'var(--accent)' }}>▋</div>}
            </div>
          ) : (
            <div style={{ color: '#61607a' }}>// Output will appear here. Click "Run connection test".</div>
          )}
        </div>
        <button onClick={onRunTest} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginTop: '14px', width: '100%', fontFamily: 'inherit', fontWeight: 600, fontSize: '13px', padding: '11px', borderRadius: '10px', cursor: testing ? 'default' : 'pointer', border: '1px solid var(--border)', background: testing ? 'var(--soft)' : 'var(--text)', color: testing ? 'var(--t2)' : 'var(--bg)' }}>
          {testing ? (
            <><svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" style={{ animation: 'tc-spin 0.8s linear infinite' }}><path d="M21 12a9 9 0 1 1-6.2-8.6" /></svg>Running…</>
          ) : (
            <><svg viewBox="0 0 24 24" width="15" height="15" fill="currentColor" stroke="none"><path d="M8 5v14l11-7z" /></svg>Run connection test</>
          )}
        </button>
      </div>
    </div>
  );
};

/* ════════════════════ TWEAKS PANEL ════════════════════ */
const TweaksPanel: React.FC<any> = ({ dark, setDark, accent, setAccent, density, setDensity, fontSize, setFontSize, close, goDashboard }) => {
  const seg = (on: boolean): CSS => ({ flex: 1, border: 'none', background: on ? 'var(--surface)' : 'transparent', color: on ? 'var(--text)' : 'var(--t3)', fontFamily: 'inherit', fontWeight: 600, fontSize: '13px', padding: '8px', borderRadius: '7px', cursor: 'pointer', boxShadow: on ? 'var(--sh-xs)' : 'none' });
  return (
    <>
      <div onClick={close} className="tc-fade" style={{ position: 'fixed', inset: 0, background: 'rgba(10,10,20,0.28)', backdropFilter: 'blur(2px)', zIndex: 80 }} />
      <div style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: '340px', maxWidth: '88vw', background: 'var(--surface)', borderLeft: '1px solid var(--border)', boxShadow: 'var(--sh-lg)', zIndex: 81, display: 'flex', flexDirection: 'column', animation: 'tc-slidein 0.26s cubic-bezier(.2,.7,.2,1) both' }}>
        <div style={{ padding: '18px 20px', borderBottom: '1px solid var(--hairline)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 700, fontSize: '15px' }}>
            <svg viewBox="0 0 24 24" width="16" height="16" fill="var(--accent)"><path d="M12 2l1.9 4.8L19 8l-3.6 3.4L16.4 17 12 14.3 7.6 17l1-5.6L5 8l5.1-1.2z" /></svg>
            Tweaks
          </div>
          <button onClick={close} className="tc-closebtn" style={{ background: 'none', border: 'none', color: 'var(--t3)', cursor: 'pointer', padding: '4px' }}><Ico d="M6 6l12 12M18 6L6 18" size={19} sw={2} /></button>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px', display: 'flex', flexDirection: 'column', gap: '22px' }}>
          <div>
            <div style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--t4)', marginBottom: '12px' }}>Appearance</div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontSize: '13.5px', fontWeight: 500 }}>Dark mode</span>
              <button onClick={() => setDark((d: boolean) => !d)} style={{ width: '44px', height: '26px', borderRadius: '999px', border: 'none', cursor: 'pointer', position: 'relative', background: dark ? 'var(--accent)' : 'var(--border)', transition: 'background 0.2s', padding: 0 }}>
                <span style={{ position: 'absolute', top: '3px', left: dark ? '21px' : '3px', width: '20px', height: '20px', borderRadius: '50%', background: '#fff', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.3)' }} />
              </button>
            </div>
          </div>
          <div>
            <div style={{ fontSize: '13.5px', fontWeight: 500, marginBottom: '10px' }}>Accent</div>
            <div style={{ display: 'flex', gap: '10px' }}>
              {Object.keys(ACCENTS).map(key => {
                const on = key === accent;
                return (
                  <button key={key} onClick={() => setAccent(key)} title={ACCENTS[key].name} style={{ width: '58px', height: '46px', borderRadius: '10px', cursor: 'pointer', background: ACCENTS[key].swatch, border: on ? '2px solid var(--text)' : '2px solid transparent', boxShadow: on ? '0 0 0 3px var(--surface), 0 0 0 5px var(--text)' : 'var(--sh-xs)', display: 'grid', placeItems: 'center', outline: 'none' }}>
                    {on && <Ico d="M20 6L9 17l-5-5" size={18} sw={3} stroke="#fff" />}
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
              <span style={{ fontSize: '13.5px', fontWeight: 500 }}>Density</span>
            </div>
            <div style={{ display: 'flex', background: 'var(--soft)', border: '1px solid var(--border)', borderRadius: '9px', padding: '3px' }}>
              <button onClick={() => setDensity('compact')} style={seg(density === 'compact')}>Compact</button>
              <button onClick={() => setDensity('regular')} style={seg(density === 'regular')}>Regular</button>
            </div>
          </div>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
              <span style={{ fontSize: '13.5px', fontWeight: 500 }}>Text size</span>
              <span style={{ fontSize: '12px', color: 'var(--t3)', fontFamily: "'Geist Mono',monospace" }}>{fontSize}px</span>
            </div>
            <input type="range" min={13} max={16} step={1} value={fontSize} onChange={e => setFontSize(+e.target.value)} style={{ width: '100%', accentColor: 'var(--accent)' }} />
          </div>
          <div>
            <div style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--t4)', marginBottom: '12px' }}>Jump to</div>
            <button onClick={goDashboard} className="tc-jumpbtn" style={{ width: '100%', textAlign: 'left', background: 'var(--soft)', border: '1px solid var(--border)', borderRadius: '9px', padding: '11px 14px', font: 'inherit', fontSize: '13.5px', fontWeight: 600, cursor: 'pointer', color: 'var(--text)' }}>Open dashboard</button>
          </div>
        </div>
      </div>
    </>
  );
};

/* ════════════════════ EVENT MODAL ════════════════════ */
const EventModal: React.FC<any> = ({ title, setTitle, date, setDate, school, setSchool, notes, setNotes, close, submit }) => {
  const input: CSS = { width: '100%', font: 'inherit', fontSize: '13.5px', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--r)', padding: '10px 12px', color: 'var(--text)', outline: 'none' };
  const fields = [
    { label: 'Title', ph: 'e.g. Sports day', full: true, val: title, set: setTitle },
    { label: 'Date', ph: '2026-07-28', full: false, val: date, set: setDate },
    { label: 'School', ph: 'All schools', full: false, val: school, set: setSchool },
    { label: 'Notes', ph: 'Optional', full: true, val: notes, set: setNotes },
  ];
  return (
    <div onClick={close} style={{ position: 'fixed', inset: 0, background: 'rgba(15,15,25,0.42)', backdropFilter: 'blur(3px)', zIndex: 90, display: 'grid', placeItems: 'center', padding: '24px', animation: 'tc-scrimin 0.18s ease' }}>
      <div onClick={e => e.stopPropagation()} style={{ width: '100%', maxWidth: '520px', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--r-xl)', boxShadow: 'var(--sh-lg)', display: 'flex', flexDirection: 'column', maxHeight: '88vh', animation: 'tc-sheetin 0.24s cubic-bezier(.2,.7,.2,1) both' }}>
        <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--hairline)', display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
          <span style={{ width: '38px', height: '38px', borderRadius: '11px', background: 'var(--accent-soft)', color: 'var(--accent)', display: 'grid', placeItems: 'center', flexShrink: 0 }}><Ico d="M4 5h16v16H4zM4 9h16M8 3v4M16 3v4" size={19} sw={2} /></span>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '16px', fontWeight: 700 }}>New event</div>
            <div style={{ fontSize: '12.5px', color: 'var(--t3)', marginTop: '2px' }}>Add to the shared calendar</div>
          </div>
          <button onClick={close} className="tc-closebtn" style={{ background: 'none', border: 'none', color: 'var(--t3)', cursor: 'pointer', padding: '4px' }}><Ico d="M6 6l12 12M18 6L6 18" size={19} sw={2} /></button>
        </div>
        <div style={{ padding: '22px 24px', overflowY: 'auto', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
          {fields.map(f => (
            <div key={f.label} style={f.full ? { gridColumn: '1 / -1' } : {}}>
              <label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--t2)', display: 'block', marginBottom: '6px' }}>{f.label}</label>
              <input className="tc-input" placeholder={f.ph} value={f.val} onChange={e => f.set(e.target.value)} style={input} />
            </div>
          ))}
        </div>
        <div style={{ padding: '16px 24px', borderTop: '1px solid var(--hairline)', display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
          <button onClick={close} className="tc-softbtn" style={{ font: 'inherit', fontSize: '13px', fontWeight: 600, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '10px', padding: '9px 16px', cursor: 'pointer', color: 'var(--text)' }}>Cancel</button>
          <button onClick={submit} className="tc-accentbtn" style={{ font: 'inherit', fontSize: '13px', fontWeight: 600, background: 'var(--accent)', color: 'var(--accent-fg)', border: '1px solid var(--accent-strong)', borderRadius: '10px', padding: '9px 16px', cursor: 'pointer', boxShadow: 'var(--sh-xs)' }}>Add event</button>
        </div>
      </div>
    </div>
  );
};

/* ════════════════════ WIZARD ════════════════════ */
const Wizard: React.FC<any> = (p) => {
  const { wStep, setWStep, wKind, setWKind, wLorry, setWLorry, wName, setWName, wCode, setWCode, wEmail, setWEmail, wAdminName, setWAdminName, wPhone, setWPhone, wAddr, setWAddr, wHome, setWHome, wPlan, pickPlan, wEndDate, setWEndDate, wActive, setWActive, wMaxStudents, setWMaxStudents, wMaxTeachers, setWMaxTeachers, wMaxParents, setWMaxParents, wDone, wBusy, wCanNext, wFullCode, mono, close, create } = p;
  const input: CSS = { width: '100%', font: 'inherit', fontSize: '13.5px', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--r)', padding: '10px 12px', color: 'var(--text)', outline: 'none' };
  const showLorry = wKind === 'LORRY';
  const isLorry = wKind === 'LORRY';
  const kindLabel = showLorry ? `Lorry · ${wLorry === 'CARGO' ? 'Cargo' : 'Personal tracking'}` : (WKINDS.find(k => k.id === wKind)!.title);
  const planDef = WPLANS.find(pl => pl.id === wPlan)!;
  const capLabel = (v: string) => (parseInt(v, 10) || 0) === 0 ? 'Unlimited' : v;
  const review = [
    { k: 'Type', v: kindLabel }, { k: 'Roles', v: WKINDS.find(k => k.id === wKind)!.roles },
    { k: 'Organization', v: wName || 'Not set' }, { k: 'Code', v: wFullCode, mono: true },
    { k: "Admin's name", v: wAdminName || 'Not set' }, { k: 'Email', v: wEmail || 'Not set' }, { k: 'Phone', v: wPhone || '—' }, { k: 'Address', v: wAddr || '—' },
    { k: 'Home location', v: wHome ? `${wHome.lat.toFixed(5)}, ${wHome.lng.toFixed(5)}` : 'Not set', mono: true },
    { k: 'Plan', v: `${planDef.name} · Ksh ${planPrice(planDef, isLorry)}${planUnitSuffix(isLorry)}` },
    { k: 'Limits', v: `${capLabel(wMaxStudents)} students · ${capLabel(wMaxParents)} parents` },
    { k: 'Billing', v: 'Beta · not billed (manual)' },
    { k: 'Status', v: wActive ? 'Active' : 'Suspended' },
  ];
  const steps = ['Type', 'Details', 'Subscription', 'Review'];
  const goStep = (n: number) => setWStep(Math.max(1, Math.min(4, n)));

  return (
    <div onClick={close} style={{ position: 'fixed', inset: 0, background: 'rgba(15,15,25,0.44)', backdropFilter: 'blur(3px)', zIndex: 90, display: 'grid', placeItems: 'center', padding: '24px', animation: 'tc-scrimin 0.18s ease' }}>
      <div onClick={e => e.stopPropagation()} style={{ width: '100%', maxWidth: '880px', height: 'min(600px,90vh)', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--r-xl)', boxShadow: 'var(--sh-lg)', display: 'flex', overflow: 'hidden', animation: 'tc-sheetin 0.24s cubic-bezier(.2,.7,.2,1) both' }}>
        {/* rail */}
        <aside style={{ width: '230px', flexShrink: 0, background: 'var(--nav-bg)', color: '#fff', padding: '24px 20px', display: 'flex', flexDirection: 'column' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '9px', fontWeight: 800, fontSize: '16px', marginBottom: '22px' }}>
            <span style={{ width: '26px', height: '26px', borderRadius: '7px', background: 'linear-gradient(140deg,var(--accent),var(--accent-strong))', display: 'grid', placeItems: 'center' }}><Ico d="M4 18 L10 10 L14 14 L20 6" size={14} sw={2.4} stroke="#fff" /></span>
            trasse
          </div>
          <div style={{ fontSize: '17px', fontWeight: 700, letterSpacing: '-0.01em' }}>New school</div>
          <div style={{ fontSize: '12.5px', color: 'var(--nav-muted)', marginTop: '5px', marginBottom: '22px', lineHeight: 1.5 }}>Same details clients enter when they sign up — provisioned by you.</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {steps.map((label, i) => {
              const n = i + 1; const on = wStep === n; const done = wStep > n;
              return (
                <button key={label} onClick={() => goStep(n)} style={{ display: 'flex', alignItems: 'center', gap: '11px', width: '100%', padding: '9px 11px', borderRadius: '9px', cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600, fontSize: '13px', textAlign: 'left', border: 'none', color: on ? '#fff' : 'var(--nav-text)', background: on ? 'color-mix(in oklab, var(--accent) 26%, transparent)' : 'transparent' }}>
                  <span style={{ width: '22px', height: '22px', borderRadius: '50%', flexShrink: 0, display: 'grid', placeItems: 'center', fontSize: '11px', fontWeight: 700, color: (on || done) ? '#fff' : 'var(--nav-muted)', background: on ? 'var(--accent)' : (done ? 'color-mix(in oklab,var(--accent) 55%, transparent)' : 'var(--nav-bg2)') }}>
                    {done ? <Ico d="M20 6L9 17l-5-5" size={12} sw={3} stroke="#fff" /> : n}
                  </span>
                  {label}
                </button>
              );
            })}
          </div>
          <div style={{ marginTop: 'auto', fontSize: '11px', color: 'var(--nav-muted)', lineHeight: 1.5 }}>Codes get a suffix automatically (.bus / .fleet / .cargo) so roles route correctly.</div>
        </aside>

        {/* body */}
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
          {wDone ? (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: '40px' }}>
              <div style={{ width: '64px', height: '64px', borderRadius: '50%', background: 'var(--green-soft)', color: 'var(--green)', display: 'grid', placeItems: 'center', marginBottom: '20px' }}><Ico d="M20 6L9 17l-5-5" size={30} sw={2.4} /></div>
              <div style={{ fontSize: '22px', fontWeight: 800, letterSpacing: '-0.02em' }}>{wName || 'The school'} is live</div>
              <div style={{ fontSize: '13.5px', color: 'var(--t3)', marginTop: '8px', maxWidth: '340px', lineHeight: 1.5 }}>The account is provisioned. Share the code and admin email so they can sign in.</div>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: '9px', marginTop: '20px', fontFamily: mono, fontSize: '15px', fontWeight: 700, background: 'var(--soft)', border: '1px solid var(--border)', borderRadius: '10px', padding: '11px 18px' }}>
                <Ico d="M3 21h18M6 21V7l6-4 6 4v14" size={16} sw={2} stroke="var(--accent)" />{wFullCode}
              </div>
              <button onClick={close} className="tc-accentbtn" style={{ marginTop: '24px', font: 'inherit', fontSize: '13.5px', fontWeight: 600, background: 'var(--accent)', color: 'var(--accent-fg)', border: '1px solid var(--accent-strong)', borderRadius: '10px', padding: '11px 20px', cursor: 'pointer' }}>Back to console</button>
            </div>
          ) : (
            <>
              <div style={{ flex: 1, overflowY: 'auto', padding: '26px 28px' }}>
                {wStep === 1 && (
                  <div>
                    <h2 style={{ margin: '0 0 4px', fontSize: '19px', fontWeight: 800, letterSpacing: '-0.02em' }}>What are you creating?</h2>
                    <p style={{ margin: '0 0 18px', fontSize: '13px', color: 'var(--t3)' }}>Sets the roles, the code suffix and the feature set.</p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      {WKINDS.map(k => {
                        const on = k.id === wKind;
                        return (
                          <button key={k.id} onClick={() => setWKind(k.id)} style={{ display: 'flex', gap: '14px', alignItems: 'flex-start', textAlign: 'left', width: '100%', padding: '15px 16px', borderRadius: 'var(--r)', cursor: 'pointer', background: on ? 'var(--accent-soft)' : 'var(--surface)', border: `1px solid ${on ? 'var(--accent)' : 'var(--border)'}`, fontFamily: 'inherit' }}>
                            <span style={{ width: '42px', height: '42px', borderRadius: '11px', flexShrink: 0, display: 'grid', placeItems: 'center', color: '#fff', background: k.grad }}><Ico d={k.d} size={20} /></span>
                            <span style={{ flex: 1 }}>
                              <span style={{ display: 'block', fontSize: '14.5px', fontWeight: 700, color: 'var(--text)' }}>{k.title}</span>
                              <span style={{ display: 'block', fontSize: '12.5px', color: 'var(--t3)', marginTop: '2px', lineHeight: 1.45 }}>{k.desc}</span>
                              <span style={{ display: 'block', fontSize: '11.5px', color: 'var(--t4)', marginTop: '6px' }}>Roles: {k.roles} · suffix <b style={{ fontFamily: mono }}>{k.suffix}</b></span>
                            </span>
                            <span style={{ width: '20px', height: '20px', borderRadius: '50%', flexShrink: 0, display: 'grid', placeItems: 'center', color: '#fff', background: on ? 'var(--accent)' : 'transparent', border: `1px solid ${on ? 'var(--accent)' : 'var(--border)'}` }}>
                              {on && <Ico d="M20 6L9 17l-5-5" size={12} sw={3} stroke="#fff" />}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                    {showLorry && (
                      <div style={{ marginTop: '16px' }}>
                        <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--t2)', marginBottom: '8px' }}>Lorry type</div>
                        <div style={{ display: 'flex', gap: '10px' }}>
                          {([['SELF_TRACKING', 'Personal tracking'], ['CARGO', 'Cargo']] as const).map(([id, label]) => {
                            const on = wLorry === id;
                            return <button key={id} onClick={() => setWLorry(id)} style={{ flex: 1, padding: '9px', borderRadius: '8px', cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600, fontSize: '13px', border: `1px solid ${on ? 'var(--accent)' : 'var(--border)'}`, background: on ? 'var(--accent-soft)' : 'var(--surface)', color: on ? 'var(--accent)' : 'var(--t2)' }}>{label}</button>;
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                )}
                {wStep === 2 && (
                  <div>
                    <h2 style={{ margin: '0 0 4px', fontSize: '19px', fontWeight: 800, letterSpacing: '-0.02em' }}>Organization details</h2>
                    <p style={{ margin: '0 0 18px', fontSize: '13px', color: 'var(--t3)' }}>The basics we'll provision the account with.</p>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                      <div style={{ gridColumn: '1 / -1' }}>
                        <label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--t2)', display: 'block', marginBottom: '6px' }}>Organization name <span style={{ color: 'var(--red)' }}>*</span></label>
                        <input className="tc-input" value={wName} onChange={e => setWName(e.target.value)} placeholder="e.g. Green Valley Academy" style={input} />
                      </div>
                      <div style={{ gridColumn: '1 / -1' }}>
                        <label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--t2)', display: 'block', marginBottom: '6px' }}>Organization code <span style={{ color: 'var(--red)' }}>*</span></label>
                        <input className="tc-input" value={wCode} onChange={e => setWCode(e.target.value)} placeholder="e.g. BROOKSIDE" style={input} />
                        <span style={{ display: 'block', fontSize: '11.5px', color: 'var(--t3)', marginTop: '6px' }}>Final code: <b style={{ fontFamily: mono, color: 'var(--accent)' }}>{wFullCode}</b></span>
                      </div>
                      <div style={{ gridColumn: '1 / -1' }}>
                        <label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--t2)', display: 'block', marginBottom: '6px' }}>Admin's name <span style={{ color: 'var(--red)' }}>*</span></label>
                        <input className="tc-input" value={wAdminName} onChange={e => setWAdminName(e.target.value)} autoComplete="name" placeholder="e.g. Grace Wanjiru" style={input} />
                        <span style={{ display: 'block', fontSize: '11.5px', color: 'var(--t3)', marginTop: '6px' }}>The person who will run the school's account. Shown in the apps instead of their email.</span>
                      </div>
                      <div>
                        <label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--t2)', display: 'block', marginBottom: '6px' }}>Email <span style={{ color: 'var(--red)' }}>*</span></label>
                        <input className="tc-input" value={wEmail} onChange={e => setWEmail(e.target.value)} type="email" placeholder="admin@school.ac.ke" style={input} />
                      </div>
                      <div>
                        <label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--t2)', display: 'block', marginBottom: '6px' }}>Phone</label>
                        <input className="tc-input" value={wPhone} onChange={e => setWPhone(e.target.value)} placeholder="+254 7XX XXX XXX" style={input} />
                      </div>
                      <div style={{ gridColumn: '1 / -1' }}>
                        <label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--t2)', display: 'block', marginBottom: '6px' }}>Address</label>
                        <input className="tc-input" value={wAddr} onChange={e => setWAddr(e.target.value)} placeholder="Street, city" style={input} />
                      </div>
                      <div style={{ gridColumn: '1 / -1' }}>
                        <label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--t2)', display: 'block', marginBottom: '6px' }}>Home location <span style={{ color: 'var(--red)' }}>*</span></label>
                        <LocationPicker value={wHome} onChange={setWHome} heightPx={240} />
                      </div>
                    </div>
                  </div>
                )}
                {wStep === 3 && (
                  <div>
                    <h2 style={{ margin: '0 0 4px', fontSize: '19px', fontWeight: 800, letterSpacing: '-0.02em' }}>Subscription</h2>
                    <p style={{ margin: '0 0 18px', fontSize: '13px', color: 'var(--t3)' }}>Pick the plan and the usage caps. Console schools are premium &amp; unbilled.</p>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '12px' }}>
                      {WPLANS.map(pl => {
                        const on = pl.id === wPlan;
                        return (
                          <button key={pl.id} onClick={() => pickPlan(pl.id)} style={{ textAlign: 'left', padding: '15px 16px', borderRadius: 'var(--r)', cursor: 'pointer', fontFamily: 'inherit', background: on ? 'var(--accent-soft)' : 'var(--surface)', border: `1px solid ${on ? 'var(--accent)' : 'var(--border)'}` }}>
                            <div style={{ fontSize: '14px', fontWeight: 700 }}>{pl.name}</div>
                            <div style={{ fontSize: '20px', fontWeight: 800, fontFamily: mono, margin: '6px 0', letterSpacing: '-0.02em' }}>Ksh {planPrice(pl, isLorry)}<span style={{ fontSize: '11px', color: 'var(--t3)', fontWeight: 500, fontFamily: 'inherit' }}> {planUnitSuffix(isLorry)}</span></div>
                            <div style={{ fontSize: '11.5px', color: 'var(--t3)', lineHeight: 1.5 }}>{pl.limits}</div>
                          </button>
                        );
                      })}
                    </div>
                    {/* usage limits — hard caps so a beta org can't invite unlimited people */}
                    <div style={{ marginTop: '18px' }}>
                      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: '8px' }}>
                        <label style={{ fontSize: '12px', fontWeight: 700, color: 'var(--t2)' }}>Usage limits</label>
                        <span style={{ fontSize: '11px', color: 'var(--t4)' }}>0 = unlimited</span>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '12px' }}>
                        {[['Max students', wMaxStudents, setWMaxStudents], ['Max teachers', wMaxTeachers, setWMaxTeachers], ['Max parents', wMaxParents, setWMaxParents]].map(([lab, val, setter]: any) => (
                          <div key={lab}>
                            <label style={{ fontSize: '11.5px', fontWeight: 600, color: 'var(--t3)', display: 'block', marginBottom: '5px' }}>{lab}</label>
                            <input className="tc-input" type="number" min={0} value={val} onChange={e => setter(e.target.value)} style={{ ...input, fontFamily: mono }} />
                          </div>
                        ))}
                      </div>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginTop: '16px', alignItems: 'end' }}>
                      <div>
                        <label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--t2)', display: 'block', marginBottom: '6px' }}>Access until <span style={{ color: 'var(--t4)', fontWeight: 500 }}>(optional)</span></label>
                        <input className="tc-input" type="date" value={wEndDate} onChange={e => setWEndDate(e.target.value)} style={input} />
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', background: 'var(--soft)', border: '1px solid var(--border)', borderRadius: 'var(--r)', padding: '10px 14px' }}>
                        <button onClick={() => setWActive((a: boolean) => !a)} style={{ width: '44px', height: '26px', borderRadius: '999px', border: 'none', cursor: 'pointer', position: 'relative', background: wActive ? 'var(--green)' : 'var(--border)', transition: 'background 0.2s', padding: 0, flexShrink: 0 }}>
                          <span style={{ position: 'absolute', top: '3px', left: wActive ? '21px' : '3px', width: '20px', height: '20px', borderRadius: '50%', background: '#fff', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.3)' }} />
                        </button>
                        <div>
                          <div style={{ fontSize: '13px', fontWeight: 700 }}>{wActive ? 'Active' : 'Inactive'}</div>
                          <div style={{ fontSize: '11.5px', color: 'var(--t3)' }}>{wActive ? 'Admin can sign in immediately' : 'Created but suspended'}</div>
                        </div>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '9px', alignItems: 'flex-start', marginTop: '16px', padding: '11px 14px', background: 'var(--accent-soft)', borderRadius: 'var(--r)', fontSize: '12px', color: 'var(--t2)', lineHeight: 1.5 }}>
                      <Ico d="M12 16v-4M12 8h.01" size={15} sw={2} stroke="var(--accent)" style={{ flexShrink: 0, marginTop: '1px' }} />
                      <span>This school is <b>premium &amp; not billed</b> (Beta / manual). It won't be charged and won't expire — the admin only loses access if you suspend it. They'll get an email invite to set a password and sign in.</span>
                    </div>
                  </div>
                )}
                {wStep === 4 && (
                  <div>
                    <h2 style={{ margin: '0 0 4px', fontSize: '19px', fontWeight: 800, letterSpacing: '-0.02em' }}>Review &amp; create</h2>
                    <p style={{ margin: '0 0 18px', fontSize: '13px', color: 'var(--t3)' }}>Confirm the details. Edit anything from the steps on the left.</p>
                    <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--r-lg)', overflow: 'hidden' }}>
                      {review.map((r, i) => (
                        <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px', padding: '12px 16px', borderBottom: '1px solid var(--hairline)' }}>
                          <span style={{ fontSize: '12.5px', color: 'var(--t3)', fontWeight: 500 }}>{r.k}</span>
                          <span style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text)', textAlign: 'right', fontFamily: (r as any).mono ? mono : 'inherit' }}>{r.v}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <div style={{ padding: '16px 24px', borderTop: '1px solid var(--hairline)', display: 'flex', gap: '10px', justifyContent: 'space-between' }}>
                <button onClick={() => (wStep === 1 ? close() : goStep(wStep - 1))} className="tc-softbtn" style={{ display: 'inline-flex', alignItems: 'center', gap: '7px', font: 'inherit', fontSize: '13px', fontWeight: 600, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '10px', padding: '10px 16px', cursor: 'pointer', color: 'var(--text)' }}>
                  <Ico d="M19 12H5M12 19l-7-7 7-7" size={15} sw={2} />{wStep === 1 ? 'Cancel' : 'Back'}
                </button>
                {wStep === 4 ? (
                  <button onClick={create} disabled={wBusy} className="tc-accentbtn" style={{ display: 'inline-flex', alignItems: 'center', gap: '7px', font: 'inherit', fontSize: '13px', fontWeight: 600, background: 'var(--accent)', color: 'var(--accent-fg)', border: '1px solid var(--accent-strong)', borderRadius: '10px', padding: '10px 18px', cursor: wBusy ? 'wait' : 'pointer', boxShadow: 'var(--sh-xs)', opacity: wBusy ? 0.7 : 1 }}>
                    <Ico d="M12 5v14M5 12h14" size={15} sw={2.2} />{wBusy ? 'Creating…' : 'Create school'}
                  </button>
                ) : (
                  <button onClick={() => (wCanNext ? goStep(wStep + 1) : null)} style={{ display: 'inline-flex', alignItems: 'center', gap: '7px', fontFamily: 'inherit', fontWeight: 600, fontSize: '13px', border: '1px solid var(--accent-strong)', borderRadius: '10px', padding: '10px 18px', cursor: wCanNext ? 'pointer' : 'not-allowed', background: 'var(--accent)', color: 'var(--accent-fg)', opacity: wCanNext ? 1 : 0.5 }}>
                    Continue <Ico d="M5 12h14M12 5l7 7-7 7" size={15} sw={2} />
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default TrasseConsole;
